import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt,
    faFileInvoice, faBox, faRupeeSign, faTag, faBalanceScale, faDollarSign,
    faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave,
    faTimes, faSpinner, faCheckCircle, faExclamationCircle, faSearch,
    faUpload, faReceipt, faChevronDown, faChevronUp, faBoxes, faBarcode,
    faFileAlt, faLayerGroup, faCubes, faBoxOpen, faPallet, faInfoCircle,
    faToggleOn, faToggleOff, faCopy, faList, faStore, faEnvelope, faBuilding,
    faAddressCard, faIdCard, faCreditCard, faMoneyBill, faUniversity, faWallet,
    faPercent, faDownload, faEdit, faChartLine, faTimesCircle, faShoppingBag,
    faHistory, faUndo, faRedo, faPrint, faFilePdf, faTrashAlt, faQrcode
} from '@fortawesome/free-solid-svg-icons';
import { SalePaymentSection, usePurchasePayments } from '../components/cosmetics_SalPaymentSection';
// import './PurchasesForm.bulk.css';

const ACCOUNTS_ENDPOINT = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_account.php';

// ==============================================
// GLOBAL BARCODE SCANNER HOOK (matches products form)
// ==============================================
const useBarcodeScanner = (onBarcodeScanned, isActive = true) => {
    const bufferRef = useRef('');
    const scanStartedAtRef = useRef(0);
    const lastTimeRef = useRef(0);
    const scanTimerRef = useRef(null);
    const isProcessingRef = useRef(false);
    const isLikelyScanningRef = useRef(false);
    const leakSnapshotRef = useRef(null);

    useEffect(() => {
        if (!isActive) return;

        const SCAN_MAX_INTERVAL_MS = 80;
        const MAX_AVERAGE_INTERVAL_MS = 60;
        const MIN_BARCODE_LENGTH = 4;
        const BUFFER_TIMEOUT_MS = 180;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        const setNativeValue = (el, value) => {
            if (!el) return;
            if (el.tagName === 'INPUT' && nativeInputValueSetter) {
                nativeInputValueSetter.call(el, value);
            } else {
                el.value = value;
            }
            el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        const restoreLeak = () => {
            const snap = leakSnapshotRef.current;
            if (snap && snap.element && snap.element.isConnected) {
                setNativeValue(snap.element, snap.value);
                if (snap.selectionStart != null && snap.element.setSelectionRange) {
                    try {
                        snap.element.setSelectionRange(snap.selectionStart, snap.selectionStart);
                    } catch (e) { }
                }
            }
            leakSnapshotRef.current = null;
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            scanStartedAtRef.current = 0;
            isLikelyScanningRef.current = false;
            leakSnapshotRef.current = null;
        };

        const commitBufferedScan = (event = null) => {
            clearTimeout(scanTimerRef.current);
            const candidate = bufferRef.current.trim();
            const averageInterval = candidate.length > 1 && scanStartedAtRef.current
                ? (lastTimeRef.current - scanStartedAtRef.current) / (candidate.length - 1)
                : Number.MAX_SAFE_INTEGER;
            const isConfirmedScan = isLikelyScanningRef.current &&
                candidate.length >= MIN_BARCODE_LENGTH &&
                averageInterval <= MAX_AVERAGE_INTERVAL_MS;

            if (isConfirmedScan) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                restoreLeak();
                if (!isProcessingRef.current) {
                    isProcessingRef.current = true;
                    // Bulk scans resolve immediately; single-mode database
                    // lookups remain serialized until their promise settles.
                    Promise.resolve(onBarcodeScanned(candidate))
                        .catch(error => console.error('Barcode handler error:', error))
                        .finally(() => { isProcessingRef.current = false; });
                }
            }

            resetBuffer();
        };

        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) {
                resetBuffer();
                return;
            }
            if (e.key === 'Shift' || e.key === 'CapsLock') {
                return;
            }

            const now = Date.now();
            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;

            const target = e.target;
            const isEditable = !!target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (e.key === 'Enter' || e.key === 'Tab') {
                if (bufferRef.current) commitBufferedScan(e);
                return;
            }

            if (e.key.length !== 1) {
                resetBuffer();
                return;
            }

            if (bufferRef.current.length === 0 || delta > SCAN_MAX_INTERVAL_MS) {
                bufferRef.current = e.key;
                scanStartedAtRef.current = now;
                isLikelyScanningRef.current = false;
                leakSnapshotRef.current = isEditable ? {
                    element: target,
                    value: target.value,
                    selectionStart: target.selectionStart
                } : null;
            } else {
                bufferRef.current += e.key;
                isLikelyScanningRef.current = true;
                if (isEditable) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }

            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(() => commitBufferedScan(), BUFFER_TIMEOUT_MS);
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [onBarcodeScanned, isActive]);
};

const formatEditableNumber = (value, decimals = 4) => {
    if (!Number.isFinite(value)) return '';
    return Number(value.toFixed(decimals)).toString();
};

const calculateProfitPercent = (cost, sell) => {
    const costValue = parseFloat(cost);
    const sellValue = parseFloat(sell);
    return costValue > 0 && Number.isFinite(sellValue) && sellValue > 0
        ? formatEditableNumber(((sellValue - costValue) / costValue) * 100)
        : '';
};

const calculateSellFromProfit = (cost, profitPercent) => {
    const costValue = parseFloat(cost);
    const profitValue = parseFloat(profitPercent);
    return Number.isFinite(costValue) && Number.isFinite(profitValue)
        ? formatEditableNumber(costValue * (1 + (profitValue / 100)), 2)
        : '';
};

// Profit percentage is optional. Blank or non-numeric input is sent as NULL
// and must never prevent the purchase from being saved.
const normalizeOptionalProfitPercent = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

// ==================== TRANSLATOR ====================
const createPurchasesTranslator = () => {
    let cache = new Map();

    const urduTranslations = {
        addPurchase: 'نیا خریداری',
        editPurchase: 'خریداری میں ترمیم کریں',
        purchaseDetails: 'خریداری کی تفصیلات',
        billNumber: 'بل نمبر',
        supplier: 'سپلائر',
        supplierName: 'سپلائر کا نام',
        phone: 'فون نمبر',
        address: 'پتہ',
        date: 'تاریخ',
        billFile: 'بل فائل',
        items: 'آئٹمز',
        addItem: 'آئٹم شامل کریں',
        productName: 'پروڈکٹ کا نام',
        model: 'ماڈل',
        serialNo: 'سیریل نمبر',
        productCode: 'پروڈکٹ کوڈ',
        batchNumber: 'بیچ نمبر',
        price: 'قیمت',
        quantity: 'مقدار',
        total: 'کل',
        remove: 'ہٹائیں',
        calculations: 'حساب',
        subtotal: 'ذیلی کل',
        grandTotal: 'کل رقم',
        paid: 'ادا کردہ رقم',
        balance: 'باقی رقم',
        savePurchase: 'خریداری محفوظ کریں',
        updatePurchase: 'خریداری اپ ڈیٹ کریں',
        clear: 'صاف کریں',
        saving: 'محفوظ ہو رہا ہے...',
        purchaseSaved: 'خریداری کامیابی سے محفوظ ہو گئی!',
        purchaseUpdated: 'خریداری کامیابی سے اپ ڈیٹ ہو گئی!',
        failedSave: 'خریداری محفوظ کرنے میں ناکامی',
        failedUpdate: 'خریداری اپ ڈیٹ کرنے میں ناکامی',
        networkError: 'نیٹ ورک کی خرابی',
        billRequired: 'بل نمبر ضروری ہے',
        supplierRequired: 'سپلائر ضروری ہے',
        dateRequired: 'تاریخ ضروری ہے',
        itemsRequired: 'کم از کم ایک آئٹم ضروری ہے',
        productRequired: 'پروڈکٹ کا نام ضروری ہے',
        batchRequired: 'بیچ نمبر ضروری ہے',
        priceRequired: 'قیمت ضروری ہے',
        quantityRequired: 'مقدار ضروری ہے',
        invalidPrice: 'براہ کرم درست قیمت درج کریں',
        invalidQuantity: 'براہ کرم درست مقدار درج کریں',
        invalidPaid: 'براہ کرم درست ادا کردہ رقم درج کریں',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        searchSupplier: 'سپلائر تلاش کریں...',
        noSuppliersFound: 'کوئی سپلائر نہیں ملا',
        category: 'زمرہ',
        company: 'کمپنی',
        packingType: 'پیکنگ کی قسم',
        subPackingType: 'ذیلی پیکنگ کی قسم',
        itemsPerPack: 'آئٹمز/پیک',
        unitOfMeasure: 'پیمائش کی اکائی',
        costPerPack: 'لاگت/پیک',
        optional: 'اختیاری',
        browse: 'براؤز کریں',
        generate: 'جنریٹ کریں',
        noFile: 'کوئی فائل منتخب نہیں',
        saveDraft: 'ڈرافٹ محفوظ کریں',
        drafts: 'ڈرافٹس',
        savedDrafts: 'محفوظ کردہ ڈرافٹس',
        noDraftsSaved: 'کوئی ڈرافٹ محفوظ نہیں',
        draftLoaded: 'ڈرافٹ لوڈ ہو گیا',
        draftCleared: 'ڈرافٹ صاف کر دیا گیا',
        clearForm: 'فارم صاف کریں',
        viewDrafts: 'ڈرافٹس دیکھیں',
        goBack: 'واپس جائیں',
        searchProducts: 'پروڈکٹس تلاش کریں',
        purchaseType: 'قسم',
        packingPurchase: 'پیک کے حساب سے خریداری',
        subPackingPurchase: 'یونٹ کے حساب سے خریداری',
        scannerReady: 'اسکینر تیار ہے',
        productFound: 'پروڈکٹ مل گیا!',
        productNotFound: 'پروڈکٹ نہیں ملا',
        barcodeSet: 'بارکوڈ سیٹ کر دیا گیا',
        scannerActive: 'اسکینر فعال ہے',
        srNo: 'شمارہ',
        costPrice: 'لاگت قیمت',
        sellPrice: 'فروخت قیمت',
        expiryDate: 'میعاد ختم ہونے کی تاریخ'
    };

    const defaultTexts = {
        addPurchase: 'New Purchase',
        editPurchase: 'Edit Purchase',
        purchaseDetails: 'Purchase Details',
        billNumber: 'Bill Number',
        supplier: 'Supplier',
        supplierName: 'Supplier Name',
        phone: 'Phone',
        address: 'Address',
        date: 'Date',
        billFile: 'Bill File',
        items: 'Items',
        addItem: 'Add Item',
        productName: 'Product Name',
        model: 'Model',
        serialNo: 'Serial No.',
        productCode: 'Product Code',
        batchNumber: 'Batch Number',
        price: 'Price',
        quantity: 'Quantity',
        total: 'Total',
        remove: 'Remove',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        grandTotal: 'Grand Total',
        paid: 'Paid Amount',
        balance: 'Balance',
        savePurchase: 'Save Purchase',
        updatePurchase: 'Update Purchase',
        clear: 'Clear',
        saving: 'Saving...',
        purchaseSaved: 'Purchase saved successfully!',
        purchaseUpdated: 'Purchase updated successfully!',
        failedSave: 'Failed to save purchase',
        failedUpdate: 'Failed to update purchase',
        networkError: 'Network error occurred',
        billRequired: 'Bill number is required',
        supplierRequired: 'Supplier is required',
        dateRequired: 'Date is required',
        itemsRequired: 'At least one item is required',
        productRequired: 'Product name is required',
        batchRequired: 'Batch number is required',
        priceRequired: 'Price is required',
        quantityRequired: 'Quantity is required',
        invalidPrice: 'Please enter a valid price',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPaid: 'Please enter a valid paid amount',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        searchSupplier: 'Search supplier...',
        noSuppliersFound: 'No suppliers found',
        category: 'Category',
        company: 'Company',
        packingType: 'Packing Type',
        subPackingType: 'Sub Packing Type',
        itemsPerPack: 'Items/Pack',
        unitOfMeasure: 'Unit of Measure',
        costPerPack: 'Cost/Pack',
        optional: 'Optional',
        browse: 'Browse',
        generate: 'Generate',
        noFile: 'No file chosen',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        searchProducts: 'Search Products',
        purchaseType: 'Type',
        packingPurchase: 'Purchase by Pack',
        subPackingPurchase: 'Purchase by Unit',
        scannerReady: 'Scanner Ready',
        productFound: 'Product found!',
        productNotFound: 'Product not found',
        barcodeSet: 'Barcode set',
        scannerActive: 'Scanner Active',
        srNo: 'Sr#',
        costPrice: 'Cost Price',
        sellPrice: 'Sell Price',
        profitPercent: 'Profit %',
        expiryDate: 'Expiry Date'
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        if (targetLang === 'ur' && urduTranslations[text]) return urduTranslations[text];
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        return translated;
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        for (const key of Object.keys(defaultTexts)) {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                translations[key] = await translateText(defaultTexts[key], lang);
            }
        }
        return translations;
    };

    return { getTranslations, translateText };
};

const purchasesTranslator = createPurchasesTranslator();

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };
    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon"><FontAwesomeIcon icon={getIconForType(toast.type)} /></div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== IOS STYLE TOGGLE SWITCH ====================
const IOSToggleSwitch = ({ checked, onChange, disabled, label, activeLabel, inactiveLabel }) => {
    return (
        <button
            type="button"
            className={`ozui-ios-toggle ${checked ? 'ozui-checked' : ''}`}
            onClick={onChange}
            disabled={disabled}
            role="switch"
            aria-checked={checked}
        >
            <span className="ozui-ios-slider" />
            <span className="ozui-ios-label">
                {checked ? (activeLabel || label || 'Buy by Pack') : (inactiveLabel || label || 'Buy by Unit')}
            </span>
        </button>
    );
};

// ==============================================
// Helper: parse numeric value from bquantity string
// ==============================================
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    return 0;
};

// ==============================================
// Helper: calculate item line total
// ==============================================
const calculateItemTotal = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    if (item.purchase_type === 'sub_packing') {
        const pricePerUnit = parseFloat(item.price) || 0;
        return (pricePerUnit * quantity).toFixed(2);
    } else {
        const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
        return (costPerPack * quantity).toFixed(2);
    }
};

// ==============================================
// Helper: Generate batch number from product name
// ==============================================
const generateBatchNumber = (productName) => {
    if (!productName) return '';
    const prefix = productName.substring(0, 3).toUpperCase();
    return `${prefix}0001`;
};

const createInitialBulkPurchaseForm = () => ({
    product_name: '',
    model: '',
    serial_no: '',
    rq: '',
    category: '',
    company: '',
    cost_price: '',
    profit_percent: '',
    sell_price: '0'
});

const generateBulkPurchaseBatchNo = (productName, shopId, rowIndex) => {
    const cleaned = (productName || 'XXX').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const prefix = (cleaned || 'XXX').substring(0, 3).padEnd(3, 'X');
    const shopPart = String(shopId || '0').replace(/\D/g, '').slice(-3).padStart(3, '0');
    const timePart = Date.now().toString(36).toUpperCase();
    const rowPart = String(rowIndex + 1).padStart(4, '0');
    const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase().padEnd(6, 'X');
    return `${prefix}-${shopPart}-${timePart}-${rowPart}-${randomPart}`;
};

const createBulkPurchaseRow = (productName, shopId, rowIndex) => ({
    batch_no: generateBulkPurchaseBatchNo(productName, shopId, rowIndex),
    total_quantity: '1',
    barcode: ''
});

// Pagination limits only mounted DOM rows. It never limits how many rows are
// kept in state, scanned, validated, or submitted.
const BULK_PURCHASE_PAGE_SIZE = 100;

// ==============================================
// Helper: Increment batch number
// ==============================================
const incrementBatchNumber = (batchNo) => {
    if (!batchNo) return '';
    const match = batchNo.match(/^([A-Za-z]+)(\d+)$/);
    if (match) {
        const prefix = match[1];
        const num = parseInt(match[2]) + 1;
        const paddedNum = String(num).padStart(match[2].length, '0');
        return `${prefix}${paddedNum}`;
    }
    return `${batchNo}0001`;
};

// ==============================================
// PACKING TYPES CONFIGURATION
// ==============================================
const packingTypesList = [
    { value: '', label: 'Select Packing Type' },
    { value: 'box', label: 'Box' },
    { value: 'carton', label: 'Carton' },
    { value: 'crate', label: 'Crate' },
    { value: 'packet', label: 'Packet' },
    { value: 'bundle', label: 'Bundle' },
    { value: 'bottle', label: 'Bottle' },
    { value: 'can', label: 'Can' }
];

const subPackingTypesMap = {
    box: [
        { value: 'bottles', label: 'Bottles', defaultQty: 24 },
        { value: 'pieces', label: 'Pieces', defaultQty: 50 },
        { value: 'cans', label: 'Cans', defaultQty: 12 },
        { value: 'packs', label: 'Packs', defaultQty: 10 }
    ],
    carton: [
        { value: 'boxes', label: 'Boxes', defaultQty: 6 },
        { value: 'pieces', label: 'Pieces', defaultQty: 100 },
        { value: 'bottles', label: 'Bottles', defaultQty: 48 }
    ],
    crate: [
        { value: 'bottles', label: 'Bottles', defaultQty: 12 },
        { value: 'cans', label: 'Cans', defaultQty: 24 },
        { value: 'pieces', label: 'Pieces', defaultQty: 30 }
    ],
    packet: [
        { value: 'pieces', label: 'Pieces', defaultQty: 20 },
        { value: 'grams', label: 'Grams', defaultQty: 500 }
    ],
    bundle: [
        { value: 'pieces', label: 'Pieces', defaultQty: 100 },
        { value: 'dozen', label: 'Dozen', defaultQty: 10 }
    ],
    bottle: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'liters', label: 'Liters', defaultQty: 1 }
    ],
    can: [
        { value: 'pieces', label: 'Pieces', defaultQty: 1 },
        { value: 'liters', label: 'Liters', defaultQty: 1 }
    ]
};

const unitsOfMeasure = [
    { value: '', label: 'Select Unit' },
    { value: 'liter_1', label: '1 Liter (1L)' },
    { value: 'liter_2', label: '2 Liters (2L)' },
    { value: 'liter_3', label: '3 Liters (3L)' },
    { value: 'liter_5', label: '5 Liters (5L)' },
    { value: 'milliliter', label: 'Milliliter (ml)' },
    { value: 'kilogram', label: 'Kilogram (kg)' },
    { value: 'gram', label: 'Gram (g)' },
    { value: 'piece', label: 'Piece (pc)' },
    { value: 'dozen', label: 'Dozen (dz)' },
    { value: 'box', label: 'Box' },
    { value: 'packet', label: 'Packet' },
    { value: 'can', label: 'Can' },
    { value: 'bottle', label: 'Bottle' }
];

// ==============================================
// CATEGORY OPTIONS (matches products form)
// ==============================================
const categoryOptions = [
    { value: '', label: 'Select Category' },
    { value: 'air_freshner', label: 'AIR FRESHNER' },
    { value: 'hand_wash', label: 'HAND WASH' },
    { value: 'body_wash', label: 'BODY WASH' },
    { value: 'hair_oil', label: 'HAIR OIL' },
    { value: 'shampoo', label: 'SHAMPOO' },
    { value: 'body_scrub', label: 'body scrub' },
    { value: 'hair_serum', label: 'HAIR SERUM' },
    { value: 'cream', label: 'cream' },
    { value: 'soft_cream', label: 'soft cream' },
    { value: 'conditioner', label: 'CONDITIONER' },
    { value: 'aster_shave', label: 'ASTER SHAVE' },
    { value: 'powder', label: 'powder' },
    { value: 'fusion_gel', label: 'fusion gel' },
    { value: 'men_shaving_foam', label: 'men shaving foam' },
    { value: 'shaving_gel', label: 'shaving gel' },
    { value: 'shaving_foam', label: 'SHAVING FOAM' },
    { value: 'hair_colour', label: 'HAIR COLOUR' },
    { value: 'speedy_hair_colour', label: 'speedy hair colour' },
    { value: 'hair_remover', label: 'HAIR REMOVER' },
    { value: 'micellar_water', label: 'micellar water' },
    { value: 'cleansing_milk', label: 'cleansing milk' },
    { value: 'mahandi', label: 'mahandi' },
    { value: 'face_wash', label: 'face wash' },
    { value: 'face_wash_pump', label: 'face wash pump' },
    { value: 'face_wash_tube', label: 'face wash tube' },
    { value: 'body_oil', label: 'body oil' },
    { value: 'men_wash', label: 'men wash' },
    { value: 'loction', label: 'LOCTION' },
    { value: 'why_beauty_face_wash', label: 'why beauty face wash' },
    { value: 'natural_cream', label: 'natural cream' },
    { value: 'fruit_lotion', label: 'fruit lotion' },
    { value: 'baby_lotion', label: 'baby lotion' },
    { value: 'top_to_wash', label: 'top to wash' },
    { value: 'face_scrub', label: 'face scrub' },
    { value: 'flawless', label: 'flawless' },
    { value: 'sparkling', label: 'sparkling' },
    { value: 'body_spray', label: 'BODY SPRAY' },
    { value: 'rollon', label: 'rollon' },
    { value: 'stick', label: 'STICK' },
    { value: 'agemiracle_serum', label: 'agemiracle serum' },
    { value: 'agemiracle_cream', label: 'agemiracle cream' },
    { value: 'hydra_miracle', label: 'hydra miracle' },
    { value: 'bright_miracle', label: 'bright miracle' },
    { value: 'fogg_daba', label: 'fogg daba' },
    { value: 'lotion_red', label: 'lotion red' },
    { value: 'mooth_wash', label: 'MOOTH WASH' },
    { value: 'diamond', label: 'diamond' },
    { value: 'toth_paste', label: 'TOTH PASTE' },
    { value: 'kids', label: 'kids' },
    { value: 'brush', label: 'Brush' },
    { value: 'toth_brush', label: 'TOTH brush' },
    { value: 'amla_hair_oil', label: 'amla hair oil' },
    { value: 'baby_shampo', label: 'baby shampo' },
    { value: 'baby_wipes', label: 'baby wipes' },
    { value: 'men_cream', label: 'men cream' },
    { value: 'sun_block', label: 'SUN BLOCK' },
    { value: 'perfume', label: 'PERFUME' },
    { value: 'pure_coconut_oil', label: 'PURE COCONUT OIL' },
    { value: 'olive_oil', label: 'OLIVE OIL' },
    { value: 'pomace_oil', label: 'POMACE OIL' },
    { value: 'ultra_marine', label: 'ULTRA MARINE' },
    { value: 'gold', label: 'GOLD' },
    { value: 'keratin_shampo', label: 'keratin shampo' },
    { value: 'baby_bath', label: 'baby bath' },
    { value: 'baby_oil', label: 'baby oil' },
    { value: 'baby_powder', label: 'baby powder' },
    { value: 'baby_soap', label: 'baby soap' },
    { value: 'feader', label: 'FEADER' },
    { value: 'pacifiers', label: 'PACIFIERS' },
    { value: 'threple', label: 'threple' },
    { value: 'age_miracle', label: 'age miracle' }
];

// ==============================================
// COLOR OPTIONS (matches products form)
// ==============================================
const colorOptions = [
    { value: '', label: 'Select Color' },
    { value: 'red', label: 'Red' },
    { value: 'blue', label: 'Blue' },
    { value: 'green', label: 'Green' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'orange', label: 'Orange' },
    { value: 'purple', label: 'Purple' },
    { value: 'pink', label: 'Pink' },
    { value: 'brown', label: 'Brown' },
    { value: 'black', label: 'Black' },
    { value: 'white', label: 'White' },
    { value: 'gray', label: 'Gray' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' },
    { value: 'beige', label: 'Beige' },
    { value: 'cream', label: 'Cream' },
    { value: 'maroon', label: 'Maroon' },
    { value: 'navy', label: 'Navy Blue' },
    { value: 'teal', label: 'Teal' },
    { value: 'turquoise', label: 'Turquoise' },
    { value: 'lime', label: 'Lime' },
    { value: 'olive', label: 'Olive' },
    { value: 'coral', label: 'Coral' },
    { value: 'salmon', label: 'Salmon' },
    { value: 'lavender', label: 'Lavender' },
    { value: 'violet', label: 'Violet' },
    { value: 'magenta', label: 'Magenta' },
    { value: 'cyan', label: 'Cyan' },
    { value: 'indigo', label: 'Indigo' },
    { value: 'amber', label: 'Amber' },
    { value: 'peach', label: 'Peach' },
    { value: 'mint', label: 'Mint' },
    { value: 'sage', label: 'Sage' },
    { value: 'tan', label: 'Tan' },
    { value: 'ivory', label: 'Ivory' },
    { value: 'charcoal', label: 'Charcoal' },
    { value: 'slate', label: 'Slate' },
    { value: 'champagne', label: 'Champagne' },
    { value: 'rose', label: 'Rose' },
    { value: 'crimson', label: 'Crimson' },
    { value: 'burgundy', label: 'Burgundy' },
    { value: 'plum', label: 'Plum' },
    { value: 'mauve', label: 'Mauve' },
    { value: 'fawn', label: 'Fawn' },
    { value: 'mustard', label: 'Mustard' },
    { value: 'rust', label: 'Rust' },
    { value: 'terracotta', label: 'Terracotta' },
    { value: 'copper', label: 'Copper' },
    { value: 'bronze', label: 'Bronze' },
    { value: 'metallic', label: 'Metallic' },
    { value: 'multi', label: 'Multi-Color' },
    { value: 'transparent', label: 'Transparent' },
    { value: 'other', label: 'Other' }
];

// ==============================================
// SIZE OPTIONS (matches products form)
// ==============================================
const sizeOptions = [
    { value: '', label: 'Select Size' },
    { value: 's', label: 'S' },
    { value: 'm', label: 'M' },
    { value: 'l', label: 'L' },
    { value: 'xl', label: 'XL' },
    { value: 'xxl', label: 'XXL' },
    { value: 'xxxl', label: 'XXXL' },
    { value: '4xl', label: '4XL' },
    { value: '5xl', label: '5XL' },
    { value: '6xl', label: '6XL' },
    { value: '7xl', label: '7XL' },
    { value: '8xl', label: '8XL' },
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
    { value: '13', label: '13' },
    { value: '14', label: '14' },
    { value: 'one_size', label: 'One Size' },
    { value: 'free_size', label: 'Free Size' },
    { value: 'adjustable', label: 'Adjustable' },
    { value: 'custom', label: 'Custom' },
    { value: 'other', label: 'Other' }
];

// ==============================================
// Helper: Calculate grand total from items
// ==============================================
const calculateGrandTotal = (items) => {
    let total = 0;
    items.forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        if ((item.purchase_type || 'packing') === 'sub_packing') {
            const pricePerUnit = parseFloat(item.price) || 0;
            total += pricePerUnit * qty;
        } else {
            const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
            total += costPerPack * qty;
        }
    });
    return total;
};

// ==================== ITEMS TABLE COMPONENT ====================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL,
    user,
    shopId,
    showToast,
    allowEmpty = false
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [products, setProducts] = useState([]);
    const [filteredProductsByIndex, setFilteredProductsByIndex] = useState({});
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [availableSubPackings, setAvailableSubPackings] = useState({});
    const productSearchRefs = useRef({});
    const productSearchTimersRef = useRef({});

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => () => {
        Object.values(productSearchTimersRef.current).forEach(timer => clearTimeout(timer));
    }, []);

    const loadProducts = async () => {
        if (!user?.username || !shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_all_products', username: user?.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success') setProducts(data.data || []);
        } catch (error) { console.error('Error loading products:', error); }
    };

    useEffect(() => { loadProducts(); }, [user?.username, shopId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        items.forEach((item, index) => {
            if (item.packing_type && subPackingTypesMap[item.packing_type]) {
                setAvailableSubPackings(prev => ({ ...prev, [index]: subPackingTypesMap[item.packing_type] }));
            } else {
                setAvailableSubPackings(prev => ({ ...prev, [index]: [] }));
            }
        });
    }, [items]);

    // Auto-generate batch number when product name is entered and no batch exists
    useEffect(() => {
        items.forEach((item, index) => {
            if (item.product_name && item.product_name.trim() !== '') {
                const hasNoBatch = !item.batch_no || item.batch_no.trim() === '';
                const isNewProduct = !item.product_id || item.product_id === '';

                if (hasNoBatch && isNewProduct) {
                    const newBatch = generateBatchNumber(item.product_name.trim());
                    if (newBatch !== item.batch_no) {
                        onItemChange(index, 'batch_no', newBatch);
                    }
                }
            }
        });
    }, [items.map(i => `${i.product_name}|${i.batch_no}|${i.product_id}`).join(',')]);

    const handleProductSearch = (index, term) => {
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));
        clearTimeout(productSearchTimersRef.current[index]);

        const item = items[index];
        if (term && term.trim() !== '' && (!item.product_id || item.product_id === '')) {
            if (!item.batch_no || item.batch_no.trim() === '') {
                const newBatch = generateBatchNumber(term.trim());
                onItemChange(index, 'batch_no', newBatch);
            }
        }

        if (term.trim() === '') {
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        } else {
            productSearchTimersRef.current[index] = setTimeout(() => {
                const query = term.trim().toLowerCase();
                const filtered = [];
                for (const product of products) {
                    if (
                        product.product_name?.toLowerCase().includes(query) ||
                        product.batch_no?.toLowerCase().includes(query) ||
                        product.barcode?.toLowerCase().includes(query) ||
                        product.product_code?.toLowerCase().includes(query)
                    ) {
                        filtered.push(product);
                        if (filtered.length >= 40) break;
                    }
                }
                setFilteredProductsByIndex(prev => ({ ...prev, [index]: filtered }));
                setShowProductDropdown(prev => ({ ...prev, [index]: filtered.length > 0 }));
            }, 100);
        }
    };

    const buildItemFromProduct = (product) => {
        let itemsPerPack = parseFloat(product.items_per_pack) || 1;
        let unitOfMeasure = product.unit_of_measure || 'piece';
        let packingType = product.packing_type || '';
        let subPackingType = product.sub_packing_type || '';
        let costPerPack = parseFloat(product.cost_per_pack) || 0;
        let costPerUnit = parseFloat(product.cost_per_unit) || 0;
        let sellPerPack = parseFloat(product.sell_per_pack) || 0;
        let sellPricePerUnit = parseFloat(product.sell_price_per_unit) || 0;
        let purchaseType = product.purchase_type || 'packing';

        if (product.packaging_info) {
            try {
                const info = typeof product.packaging_info === 'string'
                    ? JSON.parse(product.packaging_info)
                    : product.packaging_info;
                if (info) {
                    itemsPerPack = parseFloat(info.items_per_pack) || itemsPerPack;
                    unitOfMeasure = info.unit_of_measure || unitOfMeasure;
                    packingType = info.packing_type || packingType;
                    subPackingType = info.sub_packing_type || subPackingType;
                    if (!costPerPack && info.cost_per_pack) costPerPack = parseFloat(info.cost_per_pack);
                    if (!sellPerPack && info.sell_per_pack) sellPerPack = parseFloat(info.sell_per_pack);
                    purchaseType = info.purchase_type || purchaseType;
                }
            } catch (e) { }
        }

        if (!costPerUnit && costPerPack > 0 && itemsPerPack > 0) {
            costPerUnit = costPerPack / itemsPerPack;
        }
        if (!costPerUnit && product.cost_price) {
            costPerUnit = parseFloat(product.cost_price);
        }
        if (!costPerPack && costPerUnit > 0) {
            costPerPack = costPerUnit * itemsPerPack;
        }
        if (!sellPricePerUnit && sellPerPack > 0 && itemsPerPack > 0) {
            sellPricePerUnit = sellPerPack / itemsPerPack;
        }
        if (!sellPerPack && sellPricePerUnit > 0) {
            sellPerPack = sellPricePerUnit * itemsPerPack;
        }

        const displayPrice = purchaseType === 'packing'
            ? (costPerPack > 0 ? String(costPerPack) : '')
            : (costPerUnit > 0 ? String(costPerUnit) : '');
        const displaySellPrice = purchaseType === 'packing'
            ? (sellPerPack > 0 ? String(sellPerPack) : '0')
            : (sellPricePerUnit > 0 ? String(sellPricePerUnit) : '0');
        const profitPercent = product.profit_percent !== null && product.profit_percent !== undefined && product.profit_percent !== ''
            ? String(product.profit_percent)
            : calculateProfitPercent(displayPrice, displaySellPrice);

        let batchNo = product.batch_no || '';
        if (!batchNo && product.product_name) {
            batchNo = generateBatchNumber(product.product_name);
        }

        return {
            product_name: product.product_name || '',
            model: product.model || '',
            serial_no: product.serial_no || '',
            batch_no: batchNo,
            product_code: product.product_code || '',
            barcode: product.barcode || '',
            barcode_locked: false,
            category: product.category || '',
            company: product.company || '',
            color: product.color || '',
            size: product.size || '',
            purchase_type: purchaseType,
            packing_type: packingType,
            sub_packing_type: subPackingType,
            items_per_pack: itemsPerPack,
            unit_of_measure: unitOfMeasure,
            price: displayPrice,
            cost_per_pack: costPerPack > 0 ? String(costPerPack) : '',
            sell_per_pack: sellPerPack > 0 ? String(sellPerPack) : '0',
            sell_price_per_unit: sellPricePerUnit > 0 ? String(sellPricePerUnit) : '0',
            profit_percent: profitPercent,
            quantity: '1',
            product_id: product.id || '',
            _pack_price: costPerPack > 0 ? String(costPerPack) : '',
            _unit_price: costPerUnit > 0 ? String(costPerUnit) : '',
            available_stock_packs: product.quantity || 0,
            bquantity: product.bquantity || '',
            expiry_date: product.expiry_date || ''
        };
    };

    const selectProduct = (index, product) => {
        const newItem = buildItemFromProduct(product);
        onItemChange(index, 'batch_update', newItem);
        setProductSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
        setShowProductDropdown(prev => ({ ...prev, [index]: false }));

        const typeLabel = newItem.purchase_type === 'sub_packing' ? 'Sub Packing (Units)' : 'Packing (Packs)';
        const priceLabel = newItem.purchase_type === 'sub_packing'
            ? `Unit price: Rs. ${newItem.price}`
            : `Pack price: Rs. ${newItem.price}`;
        if (showToast) showToast('success', `"${product.product_name}" added — ${typeLabel} | ${priceLabel}`);
    };

    const togglePurchaseType = (index) => {
        const currentItem = items[index];
        const currentType = currentItem.purchase_type || 'packing';
        const newType = currentType === 'packing' ? 'sub_packing' : 'packing';

        let newPrice = '';
        if (newType === 'packing') {
            newPrice = currentItem._pack_price || currentItem.cost_per_pack || '';
        } else {
            newPrice = currentItem._unit_price || '';
            if (!newPrice && currentItem._pack_price && currentItem.items_per_pack) {
                const derived = parseFloat(currentItem._pack_price) / parseFloat(currentItem.items_per_pack);
                if (!isNaN(derived) && isFinite(derived)) newPrice = String(derived);
            }
        }

        onItemChange(index, 'batch_update', {
            purchase_type: newType,
            price: newPrice
        });

        if (showToast) {
            const msg = newType === 'packing'
                ? `Pack Purchase — price: Rs. ${newPrice} per pack`
                : `Unit Purchase — price: Rs. ${newPrice} per unit`;
            showToast('info', msg);
        }
    };

    const handlePackPriceChange = (index, value) => {
        const item = items[index];
        const itemsPerPack = parseFloat(item.items_per_pack) || 1;
        const updates = {
            cost_per_pack: value,
            _pack_price: value
        };
        if ((item.purchase_type || 'packing') === 'packing') {
            updates.price = value;
        }
        if (value && itemsPerPack > 0) {
            const perUnit = parseFloat(value) / itemsPerPack;
            if (!isNaN(perUnit) && isFinite(perUnit)) {
                updates._unit_price = String(perUnit);
            }
        }
        onItemChange(index, 'batch_update', updates);
    };

    const handleItemsPerPackChange = (index, value) => {
        const itemsPerPack = parseFloat(value) || 1;
        const item = items[index];
        const updates = { items_per_pack: itemsPerPack };
        if (item.cost_per_pack && itemsPerPack > 0) {
            const perUnit = parseFloat(item.cost_per_pack) / itemsPerPack;
            if (!isNaN(perUnit) && isFinite(perUnit)) {
                updates._unit_price = String(perUnit);
                if ((item.purchase_type || 'packing') === 'sub_packing') {
                    updates.price = String(perUnit);
                }
            }
        }
        onItemChange(index, 'batch_update', updates);
    };

    const handlePackingTypeChange = (index, packingType) => {
        onItemChange(index, 'batch_update', {
            packing_type: packingType,
            sub_packing_type: '',
            items_per_pack: '',
            unit_of_measure: '',
            cost_per_pack: ''
        });
    };

    const handleSubPackingChange = (index, subPackingType) => {
        const subOpt = availableSubPackings[index]?.find(sp => sp.value === subPackingType);
        const updates = { sub_packing_type: subPackingType };
        if (subOpt?.defaultQty) updates.items_per_pack = subOpt.defaultQty;
        onItemChange(index, 'batch_update', updates);
    };

    const isPackingMode = (item) => (item.purchase_type || 'packing') === 'packing';

    const getStockDisplay = (item) => {
        const packStock = parseFloat(item.available_stock_packs || item.quantity || 0);
        const unitStock = parseBQuantityNumeric(item.bquantity || '0');
        if (isPackingMode(item)) {
            return `${packStock.toFixed(0)}`;
        }
        return `${unitStock.toFixed(2)}`;
    };

    return (
        <div className="ozui-items-table-container" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
            <div className="ozui-table-header-actions" style={{ width: '100%' }}>
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.items || 'Items'}</h4>
                <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="ozui-table-wrapper" style={{ display: 'block', width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                <table className="ozui-items-table" style={{ display: 'table', width: '100%', minWidth: '100%', tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th>{translations?.srNo || 'Sr#'}</th>
                            <th>{translations?.batchNumber || 'Batch'} <span className="ozui-required">*</span></th>
                            <th>{translations?.productName || 'Product'} <span className="ozui-required">*</span></th>
                            <th>{translations?.model || 'Model'}</th>
                            <th>{translations?.productSerialNo || translations?.serialNo || 'Serial No.'}</th>
                            <th>{translations?.category || 'Category'}</th>
                            <th>{translations?.company || 'Company'}</th>
                            <th>{translations?.barcode || 'Barcode'}</th>
                            <th>{translations?.costPrice || 'Cost Price'} <span className="ozui-required">*</span></th>
                           
                            <th>{translations?.sellPrice || 'Sell Price'} <small>({translations?.optional || 'Optional'})</small></th>
                            <th>Qty <span className="ozui-required">*</span></th>
                            <th>Stock</th>
                            <th>{translations?.total || 'Total'}</th>
                          
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const filteredForIndex = filteredProductsByIndex[index] || [];
                            const showDrop = showProductDropdown[index] || false;
                            const searchTerm = productSearchTerms[index] !== undefined
                                ? productSearchTerms[index]
                                : item.product_name;
                            const isPacking = isPackingMode(item);
                            const itemTotal = calculateItemTotal(item);
                            const packLabel = item.packing_type || 'Pack';
                            const unitLabel = item.sub_packing_type || item.unit_of_measure || 'Unit';

                            return (
                                <tr key={index} className={errors[`product_name_${index}`] ? 'ozui-error-row' : ''}>
                                    <td>
                                        <div className="ozui-table-field" style={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            minWidth: '30px',
                                            width: `${Math.min(20 + (index + 1).toString().length * 10, 60)}px`,
                                            transition: 'width 0.2s ease',
                                            padding: '4px 2px'
                                        }}>
                                            {index + 1}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <input
                                                type="text"
                                                value={item.batch_no || ''}
                                                readOnly
                                                disabled={disabled || !item.product_name}
                                                placeholder={!item.product_name ? "Add product first" : "Batch no"}
                                                className={errors[`batch_no_${index}`] ? 'ozui-error' : ''}
                                                style={{
                                                    backgroundColor: item.batch_no ? '#e8f5e9' : '#f3f4f6',
                                                    minWidth: '80px',
                                                    width: item.batch_no && item.batch_no.length > 10 ? `${Math.min(item.batch_no.length * 8 + 40, 200)}px` : '100px',
                                                    transition: 'width 0.2s ease',
                                                    flex: 1,
                                                    cursor: 'default',
                                                    color: '#374151'
                                                }}
                                            />
                                            {item.product_name && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        let currentBatch = item.batch_no;
                                                        if (!currentBatch) {
                                                            currentBatch = generateBatchNumber(item.product_name);
                                                            onItemChange(index, 'batch_no', currentBatch);
                                                            if (showToast) showToast('info', `Batch generated: ${currentBatch}`);
                                                            return;
                                                        }
                                                        const newBatch = incrementBatchNumber(currentBatch);
                                                        onItemChange(index, 'batch_no', newBatch);
                                                        if (showToast) showToast('info', `Batch updated to: ${newBatch}`);
                                                    }}
                                                    style={{
                                                        background: 'none',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        color: '#059669',
                                                        fontWeight: 'bold',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '28px',
                                                        height: '32px',
                                                        transition: 'all 0.2s ease',
                                                        backgroundColor: item.batch_no ? '#f0fdf4' : '#fef3c7'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = item.batch_no ? '#dcfce7' : '#fde68a';
                                                        e.currentTarget.style.borderColor = '#059669';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = item.batch_no ? '#f0fdf4' : '#fef3c7';
                                                        e.currentTarget.style.borderColor = '#d1d5db';
                                                    }}
                                                    title={item.batch_no ? "Increment batch number" : "Generate batch number"}
                                                    disabled={disabled}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} style={{ fontSize: '10px' }} />
                                                </button>
                                            )}
                                            {errors[`batch_no_${index}`] && <div className="ozui-table-error">{errors[`batch_no_${index}`]}</div>}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field" ref={el => productSearchRefs.current[index] = el}>
                                            <div className="ozui-product-search-container" style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                                <span
                                                    ref={el => {
                                                        if (el) {
                                                            const textWidth = el.offsetWidth;
                                                            const input = el.parentElement?.querySelector('input');
                                                            if (input && textWidth > 0) {
                                                                const displayText = item.product_name || searchTerm || '';
                                                                const padding = 32;
                                                                const minWidth = 180;
                                                                const maxWidth = 450;
                                                                let newWidth = Math.max(minWidth, Math.min(textWidth + padding, maxWidth));

                                                                if (displayText.length > 10) {
                                                                    input.style.width = newWidth + 'px';
                                                                } else if (displayText.length > 0) {
                                                                    input.style.width = Math.max(minWidth, textWidth + padding) + 'px';
                                                                } else {
                                                                    input.style.width = minWidth + 'px';
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    style={{
                                                        position: 'absolute',
                                                        visibility: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '14px',
                                                        padding: '6px 10px',
                                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                        fontWeight: '400',
                                                        pointerEvents: 'none',
                                                        top: 0,
                                                        left: 0,
                                                        letterSpacing: 'normal'
                                                    }}
                                                >
                                                    {item.product_name || searchTerm || ' '}
                                                </span>

                                                <input
                                                    type="text"
                                                    value={searchTerm}
                                                    onChange={e => {
                                                        onItemChange(index, 'product_name', e.target.value);
                                                        handleProductSearch(index, e.target.value);

                                                        setTimeout(() => {
                                                            const container = e.target.parentElement;
                                                            const span = container?.querySelector('span');
                                                            const input = container?.querySelector('input');
                                                            if (span && input) {
                                                                const textWidth = span.offsetWidth;
                                                                const displayText = e.target.value || '';
                                                                const padding = 32;
                                                                const minWidth = 180;
                                                                const maxWidth = 450;

                                                                if (displayText.length > 0) {
                                                                    const newWidth = Math.max(minWidth, Math.min(textWidth + padding, maxWidth));
                                                                    input.style.width = newWidth + 'px';
                                                                } else {
                                                                    input.style.width = minWidth + 'px';
                                                                }
                                                            }
                                                        }, 10);
                                                    }}
                                                    onFocus={() => {
                                                        setShowProductDropdown(prev => ({ ...prev, [index]: true }));
                                                        const item = items[index];
                                                        if (item.product_name && item.product_name.trim() !== '' &&
                                                            (!item.batch_no || item.batch_no.trim() === '') &&
                                                            (!item.product_id || item.product_id === '')) {
                                                            const newBatch = generateBatchNumber(item.product_name.trim());
                                                            onItemChange(index, 'batch_no', newBatch);
                                                        }
                                                    }}
                                                    placeholder="Type product name or search..."
                                                    disabled={disabled}
                                                    autoComplete="off"
                                                    className={errors[`product_name_${index}`] ? 'ozui-error' : ''}
                                                    style={{
                                                        minWidth: '180px',
                                                        maxWidth: '450px',
                                                        width: (() => {
                                                            const displayText = item.product_name || searchTerm || '';
                                                            if (displayText.length > 0) {
                                                                const estimatedWidth = displayText.length * 8.5 + 32;
                                                                return Math.max(180, Math.min(estimatedWidth, 450)) + 'px';
                                                            }
                                                            return '180px';
                                                        })(),
                                                        padding: '6px 10px',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        backgroundColor: '#fff',
                                                        color: '#1f2937',
                                                        boxSizing: 'border-box',
                                                        transition: 'width 0.25s ease-in-out'
                                                    }}
                                                />

                                                {showDrop && filteredForIndex.length > 0 && (
                                                    <div className="ozui-product-dropdown-table" style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        zIndex: 1000,
                                                        backgroundColor: '#fff',
                                                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                                        maxHeight: '300px',
                                                        overflowY: 'auto',
                                                        marginTop: '4px',
                                                        minWidth: '280px'
                                                    }}>
                                                        {filteredForIndex.map(product => (
                                                            <div
                                                                key={product.id}
                                                                className="ozui-product-option"
                                                                onClick={() => {
                                                                    selectProduct(index, product);
                                                                    setTimeout(() => {
                                                                        const container = document.querySelector(`[data-index="${index}"]`);
                                                                        if (container) {
                                                                            const input = container.querySelector('input');
                                                                            const span = container.querySelector('span');
                                                                            if (input && span) {
                                                                                const textWidth = span.offsetWidth;
                                                                                const padding = 32;
                                                                                const minWidth = 180;
                                                                                const maxWidth = 450;
                                                                                const newWidth = Math.max(minWidth, Math.min(textWidth + padding, maxWidth));
                                                                                input.style.width = newWidth + 'px';
                                                                            }
                                                                        }
                                                                    }, 50);
                                                                }}
                                                                style={{
                                                                    padding: '10px 14px',
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid #f3f4f6',
                                                                    transition: 'background-color 0.15s ease'
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                            >
                                                                <div className="ozui-product-name" style={{
                                                                    fontWeight: '600',
                                                                    fontSize: '14px',
                                                                    color: '#1f2937',
                                                                    marginBottom: '4px',
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: '1.4'
                                                                }}>
                                                                    {product.product_name}
                                                                </div>
                                                                <div className="ozui-product-details" style={{
                                                                    display: 'flex',
                                                                    gap: '12px',
                                                                    fontSize: '12px',
                                                                    color: '#6b7280',
                                                                    flexWrap: 'wrap',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {product.batch_no && (
                                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <FontAwesomeIcon icon={faTag} style={{ fontSize: '10px' }} />
                                                                            {product.batch_no}
                                                                        </span>
                                                                    )}
                                                                    {product.barcode && (
                                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <FontAwesomeIcon icon={faBarcode} style={{ fontSize: '10px' }} />
                                                                            {product.barcode}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="ozui-product-item-price-stock" style={{
                                                                    display: 'flex',
                                                                    gap: '16px',
                                                                    fontSize: '12px',
                                                                    flexWrap: 'wrap'
                                                                }}>
                                                                    <span className="ozui-product-price" style={{
                                                                        color: '#059669',
                                                                        fontWeight: '600',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}>
                                                                        <FontAwesomeIcon icon={faRupeeSign} style={{ fontSize: '10px' }} />
                                                                        {' '}
                                                                        {(product.purchase_type === 'sub_packing'
                                                                            ? parseFloat(product.cost_per_unit || product.cost_price || 0)
                                                                            : parseFloat(product.cost_per_pack || product.cost_price || 0)
                                                                        ).toFixed(2)}
                                                                        {' '}
                                                                        <span style={{ fontWeight: '400', color: '#6b7280' }}>
                                                                            {product.purchase_type === 'sub_packing' ? '/unit' : '/pack'}
                                                                        </span>
                                                                    </span>
                                                                    <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`} style={{
                                                                        color: parseFloat(product.quantity) <= (product.quantity_alert || 5) ? '#dc2626' : '#6b7280',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}>
                                                                        Stock: {product.quantity} packs / {parseBQuantityNumeric(product.bquantity || '0').toFixed(2)} units
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {errors[`product_name_${index}`] && (
                                                <div className="ozui-table-error" style={{
                                                    color: '#dc2626',
                                                    fontSize: '12px',
                                                    marginTop: '4px'
                                                }}>
                                                    {errors[`product_name_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.model || ''}
                                                onChange={e => onItemChange(index, 'model', e.target.value)}
                                                disabled={disabled}
                                                placeholder={translations?.model || 'Model (optional)'}
                                                style={{ minWidth: '110px', width: '130px' }}
                                            />
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.serial_no || ''}
                                                onChange={e => onItemChange(index, 'serial_no', e.target.value)}
                                                disabled={disabled}
                                                placeholder={translations?.productSerialNo || translations?.serialNo || 'Serial No. (optional)'}
                                                style={{ minWidth: '130px', width: '150px' }}
                                            />
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.category || ''}
                                                onChange={e => onItemChange(index, 'category', e.target.value)}
                                                disabled={disabled}
                                                placeholder="Category"
                                                style={{
                                                    minWidth: '80px',
                                                    width: item.category && item.category.length > 10 ? `${Math.min(item.category.length * 8 + 40, 180)}px` : '100px',
                                                    transition: 'width 0.2s ease'
                                                }}
                                            />
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.company || ''}
                                                onChange={e => onItemChange(index, 'company', e.target.value)}
                                                disabled={disabled}
                                                placeholder={translations?.company || 'Company'}
                                                style={{
                                                    minWidth: '110px',
                                                    width: item.company && item.company.length > 12
                                                        ? `${Math.min(item.company.length * 8 + 40, 220)}px`
                                                        : '130px',
                                                    transition: 'width 0.2s ease'
                                                }}
                                            />
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.barcode || ''}
                                                onChange={e => onItemChange(index, 'barcode', e.target.value)}
                                                data-purchase-barcode-index={index}
                                                disabled={disabled}
                                                placeholder="Barcode"
                                                style={{
                                                    minWidth: '100px',
                                                    width: item.barcode && item.barcode.length > 10 ? `${Math.min(item.barcode.length * 8 + 40, 200)}px` : '120px',
                                                    transition: 'width 0.2s ease',
                                                    fontFamily: 'monospace',
                                                    letterSpacing: '1px'
                                                }}
                                            />
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.price || ''}
                                                onChange={e => onItemChange(index, 'price', e.target.value)}
                                                disabled={disabled}
                                                className={errors[`price_${index}`] ? 'ozui-error' : ''}
                                                placeholder="0.00"
                                                style={{
                                                    minWidth: '80px',
                                                    width: item.price && item.price.toString().length > 6 ? `${Math.min(item.price.toString().length * 8 + 40, 140)}px` : '100px',
                                                    transition: 'width 0.2s ease'
                                                }}
                                            />
                                            {errors[`price_${index}`] && <div className="ozui-table-error">{errors[`price_${index}`]}</div>}
                                        </div>
                                    </td>

                                  

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={isPacking ? (item.sell_per_pack || '0') : (item.sell_price_per_unit || '0')}
                                                onChange={e => onItemChange(index, 'sell_price', e.target.value)}
                                                disabled={disabled}
                                                placeholder="0.00"
                                                style={{
                                                    minWidth: '80px',
                                                    width: item.sell_per_pack && item.sell_per_pack.toString().length > 6 ? `${Math.min(item.sell_per_pack.toString().length * 8 + 40, 140)}px` : '100px',
                                                    transition: 'width 0.2s ease'
                                                }}
                                            />
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input
                                                type="text"
                                                value={item.quantity || ''}
                                                onChange={e => onItemChange(index, 'quantity', e.target.value)}
                                                disabled={disabled}
                                                className={errors[`quantity_${index}`] ? 'ozui-error' : ''}
                                                placeholder="0"
                                                style={{
                                                    minWidth: '60px',
                                                    width: item.quantity && item.quantity.toString().length > 6 ? `${Math.min(item.quantity.toString().length * 8 + 40, 120)}px` : '80px',
                                                    transition: 'width 0.2s ease'
                                                }}
                                            />
                                            {errors[`quantity_${index}`] && <div className="ozui-table-error">{errors[`quantity_${index}`]}</div>}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <span className="ozui-stock-badge" style={{
                                                fontSize: '0.75rem',
                                                minWidth: '40px',
                                                display: 'inline-block',
                                                textAlign: 'center'
                                            }}>
                                                {getStockDisplay(item)}
                                            </span>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={itemTotal} readOnly disabled className="ozui-readonly-input ozui-total-display" />
                                        </div>
                                    </td>

                                  

                                    <td>
                                        <button
                                            type="button"
                                            className="ozui-table-remove-btn"
                                            onClick={() => onRemove(index)}
                                            disabled={disabled || (!allowEmpty && items.length === 1)}
                                            title="Remove"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==================== MAIN PURCHASES COMPONENT ====================
const AppliancesPurchases = ({ purchaseId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const initialFormData = {
        date: new Date().toISOString().split('T')[0],
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        bill_file: null,
        grand_total: '0',
        paid: '0',
        balance: '0',
        items: []
    };

    const [formData, setFormData] = useState(initialFormData);
    const [entryMode, setEntryMode] = useState('single');
    const [bulkFormData, setBulkFormData] = useState(createInitialBulkPurchaseForm);
    const [bulkRows, setBulkRows] = useState([]);
    const [bulkGroups, setBulkGroups] = useState([]);
    const [bulkErrors, setBulkErrors] = useState({});
    const [bulkPage, setBulkPage] = useState(0);
    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [globalProductSearchTerm, setGlobalProductSearchTerm] = useState('');
    const [filteredGlobalProducts, setFilteredGlobalProducts] = useState([]);
    const [showGlobalProductDropdown, setShowGlobalProductDropdown] = useState(false);
    const [searchingGlobalProducts, setSearchingGlobalProducts] = useState(false);

    const purchasePayment = usePurchasePayments({
        apiUrl: ACCOUNTS_ENDPOINT,
        shopId: currentShop?.shop_id,
        username: user?.username,
        supplierId: formData.supplierid,
        purchaseId: isEditMode ? purchaseId : null,
        invoiceTotal: Number(formData.grand_total) || 0
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            paid: purchasePayment.currentInvoicePaid.toFixed(2),
            balance: purchasePayment.currentInvoiceBalance.toFixed(2)
        }));
    }, [purchasePayment.currentInvoicePaid, purchasePayment.currentInvoiceBalance]);

    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);
    const bulkCellRefs = useRef({});
    const bulkRowsRef = useRef([]);
    const pendingBulkFocusRef = useRef(null);

    const productSearchRef = useRef(null);
    const billNumberRef = useRef(null);
    const toastTimerRef = useRef(null);
    const supplierSearchRef = useRef(null);
    const fileInputRef = useRef(null);
    const isSavingRef = useRef(false);
    const isClearingRef = useRef(false);
    const isInitialLoadRef = useRef(true);
    const draftsDropdownRef = useRef(null);

    // Lock to prevent concurrent scans from causing issues
    const isAddingRef = useRef(false);
    const scanQueueRef = useRef([]);

    // ==================== TOAST ====================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }, []);

    // ==================== CALCULATE TOTALS ====================
    const calculateGrandTotal = useCallback((items) => {
        let total = 0;
        items.forEach(item => {
            const qty = parseFloat(item.quantity) || 0;
            if ((item.purchase_type || 'packing') === 'sub_packing') {
                const pricePerUnit = parseFloat(item.price) || 0;
                total += pricePerUnit * qty;
            } else {
                const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
                total += costPerPack * qty;
            }
        });
        return total;
    }, []);

    const bulkPageCount = Math.max(1, Math.ceil(bulkRows.length / BULK_PURCHASE_PAGE_SIZE));
    const safeBulkPage = Math.min(bulkPage, bulkPageCount - 1);
    const bulkPageStart = safeBulkPage * BULK_PURCHASE_PAGE_SIZE;
    const visibleBulkRows = useMemo(
        () => bulkRows.slice(bulkPageStart, bulkPageStart + BULK_PURCHASE_PAGE_SIZE),
        [bulkRows, bulkPageStart]
    );
    const scannedBulkCount = useMemo(
        () => bulkRows.reduce((count, row) => count + (row.barcode.trim() ? 1 : 0), 0),
        [bulkRows]
    );
    const bulkGrandTotal = useMemo(() => {
        const cost = parseFloat(bulkFormData.cost_price) || 0;
        return bulkRows.reduce(
            (total, row) => total + (cost * (parseFloat(row.total_quantity) || 0)),
            0
        );
    }, [bulkFormData.cost_price, bulkRows]);
    const hasStagedBulkData = useMemo(() => (
        Boolean(bulkFormData.product_name.trim()) ||
        Boolean(bulkFormData.rq) ||
        bulkRows.length > 0
    ), [bulkFormData.product_name, bulkFormData.rq, bulkRows.length]);
    const bulkGroupsGrandTotal = useMemo(() => (
        bulkGroups.reduce((total, group) => {
            const cost = parseFloat(group.header.cost_price) || 0;
            return total + group.rows.reduce(
                (groupTotal, row) => groupTotal + (cost * (parseFloat(row.total_quantity) || 0)),
                0
            );
        }, 0)
    ), [bulkGroups]);
    const committedBulkRowCount = useMemo(
        () => bulkGroups.reduce((count, group) => count + group.rows.length, 0),
        [bulkGroups]
    );
    const totalBillRowCount = formData.items.length + committedBulkRowCount +
        (hasStagedBulkData ? bulkRows.length : 0);
    const billGrandTotal = useMemo(() => (
        calculateGrandTotal(formData.items) +
        bulkGroupsGrandTotal +
        (hasStagedBulkData ? bulkGrandTotal : 0)
    ), [formData.items, bulkGroupsGrandTotal, hasStagedBulkData, bulkGrandTotal, calculateGrandTotal]);

    useEffect(() => {
        bulkRowsRef.current = bulkRows;
    }, [bulkRows]);

    useEffect(() => {
        setFormData(prev => {
            const paid = parseFloat(prev.paid) || 0;
            return {
                ...prev,
                grand_total: billGrandTotal.toFixed(2),
                balance: (billGrandTotal - paid).toFixed(2)
            };
        });
    }, [billGrandTotal]);

    const focusBulkCell = useCallback((rowIndex, field = 'barcode') => {
        if (!Number.isInteger(rowIndex) || rowIndex < 0) return;

        pendingBulkFocusRef.current = { rowIndex, field };
        setBulkPage(Math.floor(rowIndex / BULK_PURCHASE_PAGE_SIZE));

        let attempts = 0;
        const attemptFocus = () => {
            const element = bulkCellRefs.current[`${rowIndex}-${field}`];
            if (element) {
                element.focus();
                if (field === 'barcode' && element.select) element.select();
                pendingBulkFocusRef.current = null;
            } else if (pendingBulkFocusRef.current && attempts < 12) {
                attempts += 1;
                requestAnimationFrame(attemptFocus);
            } else {
                pendingBulkFocusRef.current = null;
            }
        };
        requestAnimationFrame(attemptFocus);
    }, []);

    const buildBulkPurchaseItems = useCallback((rows = bulkRowsRef.current, header = bulkFormData, groupId = null) => {
        return rows.map(row => ({
            product_name: header.product_name.trim(),
            model: header.model.trim(),
            serial_no: header.serial_no.trim(),
            batch_no: row.batch_no.trim(),
            product_code: '',
            barcode: row.barcode.trim(),
            barcode_locked: Boolean(row.barcode.trim()),
            product_id: '',
            category: header.category || '',
            company: header.company.trim(),
            color: '',
            size: '',
            price: header.cost_price,
            cost_per_pack: header.cost_price,
            sell_per_pack: header.sell_price,
            sell_price_per_unit: header.sell_price,
            profit_percent: header.profit_percent,
            quantity: row.total_quantity,
            purchase_type: 'packing',
            packing_type: '',
            sub_packing_type: '',
            items_per_pack: 1,
            unit_of_measure: 'piece',
            _pack_price: header.cost_price,
            _unit_price: header.cost_price,
            available_stock_packs: 0,
            bquantity: '',
            expiry_date: '',
            entry_kind: 'bulk',
            bulk_group_id: groupId
        }));
    }, [bulkFormData]);

    // ==================== ADD PRODUCT TO ITEMS ====================
    const addProductToItems = useCallback((product) => {
        // Prevent concurrent adds
        if (isAddingRef.current) {
            scanQueueRef.current.push(product);
            return;
        }

        isAddingRef.current = true;

        setFormData(prev => {
            // Check if product already exists
            const existingIndex = prev.items.findIndex(i => i.product_id === product.id);

            if (existingIndex >= 0) {
                const updatedItems = [...prev.items];
                const currentQty = parseFloat(updatedItems[existingIndex].quantity) || 0;
                updatedItems[existingIndex] = {
                    ...updatedItems[existingIndex],
                    barcode: product._scanned_barcode || updatedItems[existingIndex].barcode || product.barcode || '',
                    barcode_locked: Boolean(product._scanned_barcode || updatedItems[existingIndex].barcode_locked),
                    quantity: String(currentQty + 1)
                };
                const grandTotal = calculateGrandTotal(updatedItems);
                const paid = parseFloat(prev.paid) || 0;

                setTimeout(() => {
                    isAddingRef.current = false;
                    processQueue();
                }, 50);

                return {
                    ...prev,
                    items: updatedItems,
                    grand_total: grandTotal.toFixed(2),
                    balance: (grandTotal - paid).toFixed(2)
                };
            }

            // Build new item - PRESERVE THE SCANNED BARCODE
            const itemsPerPack = parseFloat(product.items_per_pack) || 1;
            const unitOfMeasure = product.unit_of_measure || 'piece';
            const packingType = product.packing_type || '';
            const subPackingType = product.sub_packing_type || '';
            const purchaseType = product.purchase_type || 'packing';

            let costPerPack = parseFloat(product.cost_per_pack) || 0;
            let costPerUnit = parseFloat(product.cost_per_unit) || parseFloat(product.cost_price) || 0;

            if (!costPerPack && costPerUnit > 0) {
                costPerPack = costPerUnit * itemsPerPack;
            }
            if (!costPerUnit && costPerPack > 0 && itemsPerPack > 0) {
                costPerUnit = costPerPack / itemsPerPack;
            }

            let sellPerPack = parseFloat(product.sell_per_pack) || 0;
            let sellPricePerUnit = parseFloat(product.sell_price_per_unit) || 0;
            if (!sellPricePerUnit && sellPerPack > 0 && itemsPerPack > 0) {
                sellPricePerUnit = sellPerPack / itemsPerPack;
            }
            if (!sellPerPack && sellPricePerUnit > 0) {
                sellPerPack = sellPricePerUnit * itemsPerPack;
            }

            const displayPrice = purchaseType === 'packing'
                ? (costPerPack > 0 ? String(costPerPack) : '')
                : (costPerUnit > 0 ? String(costPerUnit) : '');
            const displaySellPrice = purchaseType === 'packing'
                ? (sellPerPack > 0 ? String(sellPerPack) : '0')
                : (sellPricePerUnit > 0 ? String(sellPricePerUnit) : '0');
            const profitPercent = product.profit_percent !== null && product.profit_percent !== undefined && product.profit_percent !== ''
                ? String(product.profit_percent)
                : calculateProfitPercent(displayPrice, displaySellPrice);

            const batchNo = product.batch_no || generateBatchNumber(product.product_name);

            // ✅ BARCODE LOCKED: The scanned barcode is preserved and locked
            const scannedBarcode = product._scanned_barcode || product.barcode || '';
            
            const newItem = {
                product_name: product.product_name || '',
                model: product.model || '',
                serial_no: product.serial_no || '',
                batch_no: batchNo,
                product_code: product.product_code || '',
                barcode: scannedBarcode,
                barcode_locked: Boolean(scannedBarcode), // Lock if we have a barcode
                category: product.category || '',
                company: product.company || '',
                color: product.color || '',
                size: product.size || '',
                purchase_type: purchaseType,
                packing_type: packingType,
                sub_packing_type: subPackingType,
                items_per_pack: itemsPerPack,
                unit_of_measure: unitOfMeasure,
                price: displayPrice,
                cost_per_pack: costPerPack > 0 ? String(costPerPack) : '',
                sell_per_pack: sellPerPack > 0 ? String(sellPerPack) : '0',
                sell_price_per_unit: sellPricePerUnit > 0 ? String(sellPricePerUnit) : '0',
                profit_percent: profitPercent,
                quantity: '1',
                product_id: product.id || '',
                _pack_price: costPerPack > 0 ? String(costPerPack) : '',
                _unit_price: costPerUnit > 0 ? String(costPerUnit) : '',
                available_stock_packs: product.quantity || 0,
                bquantity: product.bquantity || '',
                expiry_date: product.expiry_date || ''
            };

            const newItems = [...prev.items, newItem];
            const grandTotal = calculateGrandTotal(newItems);
            const paid = parseFloat(prev.paid) || 0;

            setTimeout(() => {
                isAddingRef.current = false;
                processQueue();
            }, 50);

            return {
                ...prev,
                items: newItems,
                grand_total: grandTotal.toFixed(2),
                balance: (grandTotal - paid).toFixed(2)
            };
        });
    }, [calculateGrandTotal]);

    // Process queued scans
    const processQueue = useCallback(() => {
        if (scanQueueRef.current.length > 0) {
            const nextProduct = scanQueueRef.current.shift();
            if (nextProduct) {
                addProductToItems(nextProduct);
            }
        }
    }, [addProductToItems]);

    const addScannedManualItem = useCallback((scannedBarcode) => {
        setFormData(prev => {
            const existingIndex = prev.items.findIndex(item => item.barcode === scannedBarcode);
            if (existingIndex >= 0) return prev;

            const newItem = {
                product_name: '',
                model: '',
                serial_no: '',
                batch_no: '',
                product_code: '',
                barcode: scannedBarcode,
                barcode_locked: true, // Lock immediately
                category: '',
                company: '',
                color: '',
                size: '',
                purchase_type: 'packing',
                packing_type: '',
                sub_packing_type: '',
                items_per_pack: 1,
                unit_of_measure: 'piece',
                price: '',
                cost_per_pack: '',
                sell_per_pack: '0',
                sell_price_per_unit: '0',
                profit_percent: '',
                quantity: '1',
                product_id: '',
                _pack_price: '',
                _unit_price: '',
                available_stock_packs: 0,
                bquantity: '',
                expiry_date: ''
            };

            return { ...prev, items: [...prev.items, newItem] };
        });
    }, []);

    // When a row's Barcode input has focus, assign the scanner value to that row
    const assignBarcodeToFocusedRow = useCallback((scannedBarcode) => {
        const activeElement = document.activeElement;
        const rowIndexValue = activeElement?.dataset?.purchaseBarcodeIndex;

        if (rowIndexValue === undefined) return false;

        const rowIndex = Number(rowIndexValue);
        if (!Number.isInteger(rowIndex) || rowIndex < 0) return false;

        setFormData(prev => {
            if (!prev.items[rowIndex]) return prev;

            const items = [...prev.items];
            items[rowIndex] = {
                ...items[rowIndex],
                barcode: scannedBarcode,
                barcode_locked: true // Lock the barcode
            };

            return { ...prev, items };
        });

        return true;
    }, []);

    // ==================== HANDLE BARCODE SCAN ====================
    const handleBarcodeScan = useCallback(async (barcode) => {
        if (!barcode || barcode.trim() === '') return;
        const scannedBarcode = barcode.trim();
        setLastScannedBarcode(scannedBarcode);

        if (entryMode === 'bulk' && !isEditMode) {
            const currentRows = bulkRowsRef.current;
            if (currentRows.length === 0) {
                showToast('warning', 'Enter RQ first so the purchase rows can be created.');
                return;
            }

            // If the operator focused a barcode cell, that row wins. Otherwise
            // each scan fills the first empty barcode from top to bottom.
            const activeElement = document.activeElement;
            const focusedIndexValue = activeElement?.getAttribute?.('data-purchase-bulk-barcode-index');
            const focusedIndex = focusedIndexValue !== null && focusedIndexValue !== undefined
                ? Number(focusedIndexValue)
                : -1;
            const hasFocusedBarcode = Number.isInteger(focusedIndex) &&
                focusedIndex >= 0 && focusedIndex < currentRows.length;
            const targetIndex = hasFocusedBarcode
                ? focusedIndex
                : currentRows.findIndex(row => !row.barcode.trim());

            if (targetIndex === -1) {
                showToast('warning', 'All bulk purchase barcode rows are already filled.');
                return;
            }

            const duplicateIndex = currentRows.findIndex((row, index) =>
                index !== targetIndex &&
                row.barcode.trim().toUpperCase() === scannedBarcode.toUpperCase()
            );
            if (duplicateIndex !== -1) {
                showToast('warning', `Barcode already scanned in row ${duplicateIndex + 1}.`);
                focusBulkCell(duplicateIndex, 'barcode');
                return;
            }

            const nextRows = currentRows.slice();
            nextRows[targetIndex] = { ...nextRows[targetIndex], barcode: scannedBarcode };
            bulkRowsRef.current = nextRows;
            setBulkRows(nextRows);
            setBulkErrors(prev => ({ ...prev, rows: '', [`barcode_${targetIndex}`]: '' }));
            showToast('success', `Barcode placed in purchase row ${targetIndex + 1}.`);

            const nextEmptyAfterTarget = nextRows.findIndex((row, index) =>
                index > targetIndex && !row.barcode.trim()
            );
            const nextEmptyIndex = nextEmptyAfterTarget !== -1
                ? nextEmptyAfterTarget
                : nextRows.findIndex(row => !row.barcode.trim());
            if (nextEmptyIndex !== -1) focusBulkCell(nextEmptyIndex, 'barcode');
            return;
        }

        // First try: Assign to focused row (user entered details first, scanning last)
        if (assignBarcodeToFocusedRow(scannedBarcode)) {
            showToast('success', `${translations?.barcodeSet || 'Barcode set'}: ${scannedBarcode}`);
            return;
        }

        if (!user?.username || !currentShop?.shop_id) {
            addScannedManualItem(scannedBarcode);
            showToast('error', 'No shop selected');
            return;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record_by_barcode',
                    barcode: scannedBarcode,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success' && data.data) {
                const product = data.data;
                // Pass the scanned barcode with _scanned_barcode flag
                addProductToItems({ ...product, _scanned_barcode: scannedBarcode });
                showToast('success', `${product.product_name} - ${translations?.productFound || 'Product found!'}`);
            } else {
                const searchResponse = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'search_products',
                        searchTerm: scannedBarcode,
                        username: user.username,
                        shop_id: currentShop.shop_id
                    })
                });

                const searchData = await searchResponse.json();

                if (searchData.status === 'success' && searchData.data && searchData.data.length > 0) {
                    const product = searchData.data.find(item => item.barcode === scannedBarcode);
                    if (!product) {
                        addScannedManualItem(scannedBarcode);
                        showToast('info', `${translations?.barcodeSet || 'Barcode set'}: ${scannedBarcode}`);
                        return;
                    }
                    addProductToItems({ ...product, _scanned_barcode: scannedBarcode });
                    showToast('success', `${product.product_name} - ${translations?.productFound || 'Product found!'}`);
                } else {
                    showToast('warning', `${translations?.productNotFound || 'Product not found'}: ${scannedBarcode}`);

                    const newItem = {
                        product_name: '',
                        model: '',
                        serial_no: '',
                        batch_no: '',
                        product_code: '',
                        barcode: scannedBarcode,
                        barcode_locked: true,
                        category: '',
                        company: '',
                        color: '',
                        size: '',
                        purchase_type: 'packing',
                        packing_type: '',
                        sub_packing_type: '',
                        items_per_pack: 1,
                        unit_of_measure: 'piece',
                        price: '',
                        cost_per_pack: '',
                        sell_per_pack: '0',
                        sell_price_per_unit: '0',
                        profit_percent: '',
                        quantity: '1',
                        product_id: '',
                        _pack_price: '',
                        _unit_price: '',
                        available_stock_packs: 0,
                        bquantity: '',
                        expiry_date: ''
                    };

                    setFormData(prev => ({
                        ...prev,
                        items: [...prev.items, newItem]
                    }));
                    showToast('info', `${translations?.barcodeSet || 'Barcode set'}: ${scannedBarcode}`);
                }
            }
        } catch (error) {
            console.error('Barcode scan error:', error);
            addScannedManualItem(scannedBarcode);
            showToast('error', translations?.networkError || 'Error processing barcode scan');
        }
    }, [user?.username, currentShop?.shop_id, translations, addProductToItems, addScannedManualItem, assignBarcodeToFocusedRow, showToast, entryMode, isEditMode, focusBulkCell]);

    useBarcodeScanner(handleBarcodeScan, isScannerActive);

    // ==================== useEffect for drafts ====================
    useEffect(() => { loadDrafts(); }, [currentShop]);

    // ==================== REST OF THE COMPONENT ====================
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowSupplierDropdown(false);
                setShowGlobalProductDropdown(false);
                setShowDraftsList(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    useEffect(() => {
        setTimeout(() => { if (billNumberRef.current) billNumberRef.current.focus(); }, 100);
    }, []);

    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `purchase_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = formData.bill_no?.trim() || formData.supplierid ||
            formData.items?.some(i => i.product_name?.trim()) ||
            bulkGroups.length > 0 || hasStagedBulkData;
        if (!hasData) { showToast('warning', 'Cannot save empty draft'); return; }
        const draft = {
            id: Date.now(),
            ...formData,
            entryMode,
            bulkFormData,
            bulkRows: bulkRowsRef.current,
            bulkGroups,
            paymentLines: purchasePayment.payments,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };
        const existing = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existing.unshift(draft);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(existing.slice(0, 20)));
        showToast('success', 'Draft saved!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        setSavedDrafts(currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts);
    };

    const loadDraft = (draft) => {
        setFormData({ ...initialFormData, ...draft, date: draft.date || new Date().toISOString().split('T')[0], items: draft.items || [] });
        const draftMode = draft.entryMode === 'bulk' ? 'bulk' : 'single';
        const restoredBulkRows = Array.isArray(draft.bulkRows) ? draft.bulkRows : [];
        const restoredBulkGroups = Array.isArray(draft.bulkGroups) ? draft.bulkGroups : [];
        setEntryMode(draftMode);
        setBulkFormData({ ...createInitialBulkPurchaseForm(), ...(draft.bulkFormData || {}) });
        bulkRowsRef.current = restoredBulkRows;
        setBulkRows(restoredBulkRows);
        setBulkGroups(restoredBulkGroups);
        purchasePayment.setPayments(Array.isArray(draft.paymentLines) && draft.paymentLines.length
            ? draft.paymentLines
            : [{ payment_type: 'cash', account_id: '', amount: '', reference_no: '' }]);
        setBulkErrors({});
        setBulkPage(0);
        if (draft.supplier_name) setSearchTerm(draft.supplier_name);
        setShowDraftsList(false);
        showToast('success', 'Draft loaded!');
        setTimeout(() => {
            if (draftMode === 'bulk') document.getElementById('purchase_bulk_product_name')?.focus();
            else if (billNumberRef.current) billNumberRef.current.focus();
        }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(drafts.filter(d => d.id !== draftId)));
        loadDrafts();
        showToast('success', 'Draft deleted!');
    };

    const clearDraft = () => {
        if (isSavingRef.current) return;
        isClearingRef.current = true;
        setFormData({ ...initialFormData, items: [] });
        setBulkFormData(createInitialBulkPurchaseForm());
        bulkRowsRef.current = [];
        setBulkRows([]);
        setBulkGroups([]);
        setBulkErrors({});
        setBulkPage(0);
        bulkCellRefs.current = {};
        setSearchTerm('');
        purchasePayment.resetPayments();
        setErrors({});
        setIsEditMode(false);
        setGlobalProductSearchTerm('');
        setLastScannedBarcode('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        showToast('success', 'Form cleared');
        setTimeout(() => {
            if (entryMode === 'bulk') document.getElementById('purchase_bulk_product_name')?.focus();
            else if (billNumberRef.current) billNumberRef.current.focus();
        }, 100);
        setTimeout(() => { isClearingRef.current = false; }, 500);
    };

    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        applyTheme(localStorage.getItem('ourzest_theme') || '#6366f1');
        const hs = (e) => { if (e.key === 'ourzest_theme') applyTheme(e.newValue || '#6366f1'); };
        const ht = (e) => { if (e.detail?.theme) applyTheme(e.detail.theme); };
        window.addEventListener('storage', hs);
        window.addEventListener('themeChanged', ht);
        return () => { window.removeEventListener('storage', hs); window.removeEventListener('themeChanged', ht); };
    }, [applyTheme]);

    useEffect(() => {
        const sync = () => {
            const dark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(dark);
            document.body.classList.toggle('ozui-dark', dark);
            document.documentElement.classList.toggle('ozui-dark', dark);
        };
        sync();
        const hs = (e) => { if (e.key === 'ourzest_darkMode') sync(); };
        window.addEventListener('storage', hs);
        window.addEventListener('darkModeToggled', sync);
        return () => { window.removeEventListener('storage', hs); window.removeEventListener('darkModeToggled', sync); };
    }, []);

    useEffect(() => {
        const load = async (lang) => { setTranslations(await purchasesTranslator.getTranslations(lang)); };
        load(currentLanguage);
        const hs = () => { const l = localStorage.getItem('ourzest_language') || 'en'; if (l !== currentLanguage) setCurrentLanguage(l); };
        const hc = (e) => { if (e.detail?.language && e.detail.language !== currentLanguage) setCurrentLanguage(e.detail.language); };
        window.addEventListener('storage', hs);
        window.addEventListener('languageChanged', hc);
        const iv = setInterval(() => {
            const l = localStorage.getItem('ourzest_language') || 'en';
            if (l !== currentLanguage) setCurrentLanguage(l);
        }, 1000);
        return () => { window.removeEventListener('storage', hs); window.removeEventListener('languageChanged', hc); clearInterval(iv); };
    }, [currentLanguage]);

    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(e.target)) setShowDraftsList(false);
            if (supplierSearchRef.current && !supplierSearchRef.current.contains(e.target)) setShowSupplierDropdown(false);
            if (productSearchRef.current && !productSearchRef.current.contains(e.target)) setShowGlobalProductDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }; }, []);

    const loadSuppliers = async () => {
        if (!user?.username || !currentShop?.shop_id) return;
        try {
            const r = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_suppliers', searchTerm: '', username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success' && d.data) { setSuppliers(d.data); setFilteredSuppliers(d.data); }
        } catch (e) { console.error('Error loading suppliers:', e); }
    };

    useEffect(() => { loadSuppliers(); }, [user?.username, currentShop?.shop_id]);
    useEffect(() => { if (purchaseId) { setIsEditMode(true); loadPurchaseData(purchaseId); } }, [purchaseId]);

    const loadPurchaseData = async (id) => {
        setLoading(true);
        try {
            const r = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_purchases.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id, username: user?.username, shop_id: currentShop?.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success' && d.data) {
                const p = d.data;
                purchasePayment.loadSavedPurchase(p);
                setEntryMode('single');
                setBulkFormData(createInitialBulkPurchaseForm());
                bulkRowsRef.current = [];
                setBulkRows([]);
                setBulkGroups([]);
                setFormData({
                    date: p.date || new Date().toISOString().split('T')[0],
                    bill_no: p.bill_no || '', supplierid: p.supplierid || '',
                    supplier_name: p.supplier_name || '', phone: p.phone || '',
                    address: p.address || '', bill_file: null,
                    grand_total: p.grand_total || '0.00', paid: p.paid || '0.00',
                    balance: p.balance || '0.00',
                    items: (p.items || []).map(item => ({
                        ...item,
                        barcode_locked: Boolean(item.barcode),
                        profit_percent: item.profit_percent !== null && item.profit_percent !== undefined && item.profit_percent !== ''
                            ? String(item.profit_percent)
                            : calculateProfitPercent(
                                item.price,
                                item.purchase_type === 'sub_packing' ? item.sell_price_per_unit : item.sell_per_pack
                            ),
                        purchase_type: item.purchase_type || 'packing',
                        _pack_price: item.purchase_type === 'packing'
                            ? (item.cost_per_pack || item.price || '')
                            : String(parseFloat(item.price || 0) * parseFloat(item.items_per_pack || 1)),
                        _unit_price: item.purchase_type === 'sub_packing'
                            ? (item.price || '')
                            : String(parseFloat(item.cost_per_pack || 0) / parseFloat(item.items_per_pack || 1))
                    }))
                });
                if (p.supplier_name) setSearchTerm(p.supplier_name);
            } else {
                showToast('error', d.message || 'Failed to load purchase data');
            }
        } catch (e) { showToast('error', 'Network error'); }
        finally { setLoading(false); }
    };

    const searchGlobalProducts = useCallback(async (term) => {
        if (!term || term.length < 2 || !user?.username || !currentShop?.shop_id) {
            setFilteredGlobalProducts([]); setShowGlobalProductDropdown(false); return;
        }
        setSearchingGlobalProducts(true);
        try {
            const r = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: term, username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success') {
                const products = (d.data || []).map(p => {
                    let itemsPerPack = parseFloat(p.items_per_pack) || 1;
                    let unitOfMeasure = p.unit_of_measure || 'piece';
                    let packingType = p.packing_type || '';
                    let subPackingType = p.sub_packing_type || '';
                    let costPerPack = parseFloat(p.cost_per_pack) || 0;
                    let costPerUnit = parseFloat(p.cost_per_unit) || 0;
                    let sellPerPack = parseFloat(p.sell_per_pack) || 0;
                    let purchaseType = p.purchase_type || 'packing';

                    if (p.packaging_info) {
                        try {
                            const info = typeof p.packaging_info === 'string' ? JSON.parse(p.packaging_info) : p.packaging_info;
                            if (info) {
                                itemsPerPack = parseFloat(info.items_per_pack) || itemsPerPack;
                                unitOfMeasure = info.unit_of_measure || unitOfMeasure;
                                packingType = info.packing_type || packingType;
                                subPackingType = info.sub_packing_type || subPackingType;
                                if (!costPerPack && info.cost_per_pack) costPerPack = parseFloat(info.cost_per_pack);
                                if (!sellPerPack && info.sell_per_pack) sellPerPack = parseFloat(info.sell_per_pack);
                                purchaseType = info.purchase_type || purchaseType;
                            }
                        } catch (e) { }
                    }

                    if (!costPerUnit && costPerPack > 0 && itemsPerPack > 0) costPerUnit = costPerPack / itemsPerPack;
                    if (!costPerUnit && p.cost_price) costPerUnit = parseFloat(p.cost_price);
                    if (!costPerPack && costPerUnit > 0) costPerPack = costPerUnit * itemsPerPack;

                    return {
                        id: p.id,
                        product_name: p.product_name || '',
                        model: p.model || '',
                        serial_no: p.serial_no || '',
                        product_code: p.product_code || '',
                        batch_no: p.batch_no || '',
                        barcode: p.barcode || '',
                        barcode_locked: false,
                        sell_price: parseFloat(p.sell_price) || 0,
                        quantity: parseFloat(p.quantity) || 0,
                        bquantity: p.bquantity || '',
                        quantity_alert: parseFloat(p.quantity_alert) || 5,
                        category: p.category || '',
                        company: p.company || '',
                        color: p.color || '',
                        size: p.size || '',
                        packing_type: packingType,
                        sub_packing_type: subPackingType,
                        items_per_pack: itemsPerPack,
                        unit_of_measure: unitOfMeasure,
                        cost_price: parseFloat(p.cost_price) || 0,
                        cost_per_pack: costPerPack,
                        cost_per_unit: costPerUnit,
                        sell_per_pack: sellPerPack,
                        sell_price_per_unit: parseFloat(p.sell_price_per_unit) || 0,
                        profit_percent: p.profit_percent ?? '',
                        purchase_type: purchaseType,
                        packaging_info: p.packaging_info,
                        expiry_date: p.expiry_date || ''
                    };
                });
                setFilteredGlobalProducts(products);
                setShowGlobalProductDropdown(products.length > 0);
            } else {
                setFilteredGlobalProducts([]); setShowGlobalProductDropdown(false);
            }
        } catch (e) {
            console.error('Product search error:', e);
            setFilteredGlobalProducts([]); setShowGlobalProductDropdown(false);
        } finally { setSearchingGlobalProducts(false); }
    }, [user?.username, currentShop?.shop_id]);

    // Manual add item function with immediate calculation
    const addManualItem = () => {
        const newItem = {
            product_name: '',
            model: '',
            serial_no: '',
            batch_no: '',
            product_code: '',
            barcode: '',
            barcode_locked: false,
            product_id: '',
            category: '',
            company: '',
            color: '',
            size: '',
            price: '',
            quantity: '1',
            purchase_type: 'packing',
            packing_type: '',
            sub_packing_type: '',
            items_per_pack: 1,
            unit_of_measure: 'piece',
            cost_per_pack: '',
            sell_per_pack: '0',
            sell_price_per_unit: '0',
            profit_percent: '',
            _pack_price: '',
            _unit_price: '',
            available_stock_packs: 0,
            bquantity: '',
            expiry_date: ''
        };

        const newItems = [...formData.items, newItem];
        const grandTotal = calculateGrandTotal(newItems);
        const paid = parseFloat(formData.paid) || 0;
        setFormData(prev => ({
            ...prev,
            items: newItems,
            grand_total: grandTotal.toFixed(2),
            balance: (grandTotal - paid).toFixed(2)
        }));

        setTimeout(() => {
            const inputs = document.querySelectorAll('.ozui-items-table tbody tr:last-child input[type="text"]');
            if (inputs.length > 0) {
                inputs[0].focus();
            }
        }, 100);
    };

    const handleSupplierSearch = async (term) => {
        setSearchTerm(term);
        setShowSupplierDropdown(true);
        if (!user?.username || !currentShop?.shop_id) return;
        try {
            const r = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_suppliers', searchTerm: term, username: user.username, shop_id: currentShop.shop_id })
            });
            const d = await r.json();
            if (d.status === 'success' && d.data) setFilteredSuppliers(d.data);
        } catch (e) { console.error('Supplier search error:', e); }
    };

    const selectSupplier = (supplier) => {
        setFormData(prev => ({
            ...prev, supplierid: supplier.supplierid,
            supplier_name: supplier.supplier_name || '',
            phone: supplier.phone || '', address: supplier.address || ''
        }));
        setSearchTerm(supplier.supplier_name || '');
        setBulkFormData(prev => ({
            ...prev,
            company: prev.company || supplier.company || ''
        }));
        setShowSupplierDropdown(false);
        if (errors.supplier) setErrors(prev => ({ ...prev, supplier: '' }));
        showToast('success', `Supplier "${supplier.supplier_name}" selected`);
    };

    // ✅ FIXED: Enhanced handleItemChange with BARCODE LOCK protection
    const handleItemChange = (index, field, value) => {
        if (field === 'batch_update') {
            setFormData(prev => {
                const items = [...prev.items];
                const currentItem = items[index];
                const hasLockedBarcode = currentItem.barcode_locked && currentItem.barcode;
                
                items[index] = {
                    ...currentItem,
                    ...value,
                    // NEVER overwrite a locked barcode
                    barcode: hasLockedBarcode ? currentItem.barcode : (value.barcode || currentItem.barcode || ''),
                    barcode_locked: Boolean(hasLockedBarcode || value.barcode_locked)
                };
                const grandTotal = calculateGrandTotal(items);
                const paid = parseFloat(prev.paid) || 0;
                return {
                    ...prev,
                    items,
                    grand_total: grandTotal.toFixed(2),
                    balance: (grandTotal - paid).toFixed(2)
                };
            });
        } else {
            setFormData(prev => {
                const items = [...prev.items];
                const cur = items[index];
                const updated = { ...cur, [field]: value };

                // Manual typing/editing remains allowed. barcode_locked only
                // prevents a later product lookup from replacing the value.
                if (field === 'barcode') {
                    updated.barcode = value;
                    updated.barcode_locked = Boolean(value && value.trim());
                }

                // Auto-generate batch number when product name changes for new products
                if (field === 'product_name' && value && value.trim() !== '') {
                    const isNewProduct = !cur.product_id || cur.product_id === '';
                    const hasNoBatch = !cur.batch_no || cur.batch_no.trim() === '';

                    if (isNewProduct && hasNoBatch) {
                        updated.batch_no = generateBatchNumber(value.trim());
                    }
                }

                // Handle price changes
                if (field === 'price') {
                    const priceVal = value;
                    const isPacking = (cur.purchase_type || 'packing') === 'packing';

                    if (isPacking) {
                        updated.cost_per_pack = priceVal;
                        updated._pack_price = priceVal;

                        const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                        if (priceVal && itemsPerPack > 0) {
                            const perUnit = parseFloat(priceVal) / itemsPerPack;
                            if (!isNaN(perUnit) && isFinite(perUnit)) {
                                updated._unit_price = String(perUnit);
                            }
                        }
                    } else {
                        updated._unit_price = priceVal;

                        const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                        if (priceVal && itemsPerPack > 0) {
                            const packPrice = parseFloat(priceVal) * itemsPerPack;
                            if (!isNaN(packPrice) && isFinite(packPrice)) {
                                updated.cost_per_pack = String(packPrice);
                                updated._pack_price = String(packPrice);
                            }
                        }
                    }

                    if (cur.profit_percent !== '' && cur.profit_percent !== null && cur.profit_percent !== undefined) {
                        const calculatedSell = calculateSellFromProfit(priceVal, cur.profit_percent);
                        const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                        if (calculatedSell !== '') {
                            if (isPacking) {
                                updated.sell_per_pack = calculatedSell;
                                updated.sell_price_per_unit = formatEditableNumber(parseFloat(calculatedSell) / itemsPerPack, 4);
                            } else {
                                updated.sell_price_per_unit = calculatedSell;
                                updated.sell_per_pack = formatEditableNumber(parseFloat(calculatedSell) * itemsPerPack, 2);
                            }
                        }
                    }
                }

                if (field === 'profit_percent') {
                    updated.profit_percent = value;
                    const calculatedSell = calculateSellFromProfit(cur.price, value);
                    const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                    const isPacking = (cur.purchase_type || 'packing') === 'packing';
                    if (calculatedSell !== '') {
                        if (isPacking) {
                            updated.sell_per_pack = calculatedSell;
                            updated.sell_price_per_unit = formatEditableNumber(parseFloat(calculatedSell) / itemsPerPack, 4);
                        } else {
                            updated.sell_price_per_unit = calculatedSell;
                            updated.sell_per_pack = formatEditableNumber(parseFloat(calculatedSell) * itemsPerPack, 2);
                        }
                    }
                }

                if (field === 'sell_price') {
                    const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                    const isPacking = (cur.purchase_type || 'packing') === 'packing';
                    if (isPacking) {
                        updated.sell_per_pack = value;
                        updated.sell_price_per_unit = value === '' ? '' : formatEditableNumber(parseFloat(value) / itemsPerPack, 4);
                    } else {
                        updated.sell_price_per_unit = value;
                        updated.sell_per_pack = value === '' ? '' : formatEditableNumber(parseFloat(value) * itemsPerPack, 2);
                    }
                    updated.profit_percent = calculateProfitPercent(cur.price, value);
                }

                if (field === 'cost_per_pack') {
                    const packPrice = value;
                    updated._pack_price = packPrice;

                    const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                    if (packPrice && itemsPerPack > 0) {
                        const perUnit = parseFloat(packPrice) / itemsPerPack;
                        if (!isNaN(perUnit) && isFinite(perUnit)) {
                            updated._unit_price = String(perUnit);

                            if ((cur.purchase_type || 'packing') === 'packing') {
                                updated.price = packPrice;
                            }
                        }
                    }
                }

                if (field === 'items_per_pack') {
                    const itemsPerPack = parseFloat(value) || 1;
                    updated.items_per_pack = itemsPerPack;

                    const isPacking = (cur.purchase_type || 'packing') === 'packing';
                    const currentPrice = parseFloat(cur.price) || 0;

                    if (isPacking && currentPrice > 0) {
                        const perUnit = currentPrice / itemsPerPack;
                        if (!isNaN(perUnit) && isFinite(perUnit)) {
                            updated._unit_price = String(perUnit);
                        }
                    } else if (!isPacking && currentPrice > 0) {
                        const packPrice = currentPrice * itemsPerPack;
                        if (!isNaN(packPrice) && isFinite(packPrice)) {
                            updated.cost_per_pack = String(packPrice);
                            updated._pack_price = String(packPrice);
                        }
                    }
                }

                if (field === 'sell_per_pack') {
                    const sellPack = value;
                    updated.sell_per_pack = sellPack;

                    const itemsPerPack = parseFloat(cur.items_per_pack) || 1;
                    if (sellPack && itemsPerPack > 0) {
                        const sellPerUnit = parseFloat(sellPack) / itemsPerPack;
                        if (!isNaN(sellPerUnit) && isFinite(sellPerUnit)) {
                            updated.sell_price_per_unit = String(sellPerUnit);
                        }
                    }
                }

                if (field === 'quantity') {
                    if (value === '' || value === null || value === undefined) {
                        updated.quantity = '';
                    } else {
                        const qty = parseFloat(value);
                        if (!isNaN(qty) && qty >= 0) {
                            updated.quantity = String(qty);
                        } else {
                            updated.quantity = value;
                        }
                    }
                }

                items[index] = updated;
                const grandTotal = calculateGrandTotal(items);
                const paid = parseFloat(prev.paid) || 0;
                return {
                    ...prev,
                    items,
                    grand_total: grandTotal.toFixed(2),
                    balance: (grandTotal - paid).toFixed(2)
                };
            });

            const errKey = `${field}_${index}`;
            if (errors[errKey]) setErrors(prev => { const e = { ...prev }; delete e[errKey]; return e; });
        }
    };

    const addItem = () => {
        addManualItem();
    };

    const removeItem = (index) => {
        let items;
        if (formData.items.length > 1) {
            items = [...formData.items];
            items.splice(index, 1);
        } else {
            items = [];
        }
        const grandTotal = calculateGrandTotal(items);
        const paid = parseFloat(formData.paid) || 0;
        setFormData(prev => ({
            ...prev,
            items,
            grand_total: grandTotal.toFixed(2),
            balance: (grandTotal - paid).toFixed(2)
        }));
    };

    const handleEntryModeChange = (mode) => {
        if (mode === entryMode || saving || loading) return;
        if (isEditMode && mode === 'bulk') {
            showToast('warning', 'Finish or clear the current purchase edit before opening Bulk mode.');
            return;
        }

        setEntryMode(mode);
        setErrors({});
        setBulkErrors({});
        setLastScannedBarcode('');
        setBulkPage(0);

        setTimeout(() => {
            if (mode === 'bulk') document.getElementById('purchase_bulk_product_name')?.focus();
            else document.getElementById('global_product_search')?.focus();
        }, 0);
    };

    const handleBulkFieldChange = (e) => {
        const { name, value } = e.target;

        if (name === 'rq') {
            const parsed = value === '' ? 0 : Number(value);
            const rowCount = Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 0;
            setBulkFormData(prev => ({ ...prev, rq: value }));
            const currentRows = bulkRowsRef.current;
            const nextRows = Array.from({ length: rowCount }, (_, index) =>
                currentRows[index] || createBulkPurchaseRow(
                    bulkFormData.product_name,
                    currentShop?.shop_id,
                    index
                )
            );
            bulkRowsRef.current = nextRows;
            setBulkRows(nextRows);
            setBulkPage(0);
        } else if (name === 'product_name') {
            setBulkFormData(prev => ({ ...prev, product_name: value }));
            setBulkRows(prevRows => {
                const nextRows = prevRows.map((row, index) => ({
                    ...row,
                    batch_no: generateBulkPurchaseBatchNo(value, currentShop?.shop_id, index)
                }));
                bulkRowsRef.current = nextRows;
                return nextRows;
            });
        } else if (name === 'cost_price') {
            setBulkFormData(prev => {
                const next = { ...prev, cost_price: value };
                const cost = parseFloat(value);
                const profit = parseFloat(prev.profit_percent);
                if (Number.isFinite(cost) && Number.isFinite(profit)) {
                    next.sell_price = calculateSellFromProfit(cost, profit);
                }
                return next;
            });
        } else if (name === 'profit_percent') {
            setBulkFormData(prev => {
                const next = { ...prev, profit_percent: value };
                const cost = parseFloat(prev.cost_price);
                const profit = parseFloat(value);
                if (Number.isFinite(cost) && Number.isFinite(profit)) {
                    next.sell_price = calculateSellFromProfit(cost, profit);
                }
                return next;
            });
        } else if (name === 'sell_price') {
            setBulkFormData(prev => ({
                ...prev,
                sell_price: value,
                profit_percent: calculateProfitPercent(prev.cost_price, value)
            }));
        } else {
            setBulkFormData(prev => ({ ...prev, [name]: value }));
        }

        if (bulkErrors[name]) {
            setBulkErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleBulkRowChange = (rowIndex, field, value) => {
        setBulkRows(prev => {
            const nextRows = prev.map((row, index) =>
                index === rowIndex ? { ...row, [field]: value } : row
            );
            bulkRowsRef.current = nextRows;
            return nextRows;
        });
        setBulkErrors(prev => ({
            ...prev,
            rows: '',
            [`${field}_${rowIndex}`]: ''
        }));
    };

    const handleBulkCellKeyDown = (event, rowIndex, field) => {
        if (event.key !== 'Tab' || event.shiftKey || field !== 'company') return;
        if (rowIndex >= bulkRowsRef.current.length - 1) return;
        event.preventDefault();
        focusBulkCell(rowIndex + 1, 'batch_no');
    };

    const validateBulkForm = () => {
        const nextErrors = {};
        const rq = Number(bulkFormData.rq);

        if (!bulkFormData.product_name.trim() || bulkFormData.product_name.trim().length < 2) {
            nextErrors.product_name = 'Product name must be at least 2 characters.';
        }
        if (!Number.isSafeInteger(rq) || rq < 1) {
            nextErrors.rq = 'RQ must be a positive whole number.';
        }
        if (bulkRowsRef.current.length !== rq) {
            nextErrors.rows = 'RQ does not match the generated rows.';
        }
        if (!bulkFormData.cost_price || parseFloat(bulkFormData.cost_price) <= 0) {
            nextErrors.cost_price = 'Cost price must be greater than zero.';
        }
        const seenBatches = new Map();
        const seenBarcodes = new Map();
        let firstInvalidRow = -1;

        bulkRowsRef.current.forEach((row, index) => {
            const batchNo = row.batch_no.trim().toUpperCase();
            const barcode = row.barcode.trim().toUpperCase();
            const quantity = parseFloat(row.total_quantity);

            if (!batchNo) {
                nextErrors[`batch_no_${index}`] = 'Batch number is required.';
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else if (seenBatches.has(batchNo)) {
                nextErrors[`batch_no_${index}`] = `Duplicates row ${seenBatches.get(batchNo) + 1}.`;
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else {
                seenBatches.set(batchNo, index);
            }

            if (!barcode) {
                nextErrors[`barcode_${index}`] = 'Scan or enter a barcode.';
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else if (seenBarcodes.has(barcode)) {
                nextErrors[`barcode_${index}`] = `Already used in row ${seenBarcodes.get(barcode) + 1}.`;
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else {
                seenBarcodes.set(barcode, index);
            }

            if (!Number.isFinite(quantity) || quantity <= 0) {
                nextErrors[`total_quantity_${index}`] = 'Quantity must be greater than zero.';
                if (firstInvalidRow === -1) firstInvalidRow = index;
            }
        });

        if (firstInvalidRow !== -1) {
            nextErrors.rows = 'Fix the highlighted bulk purchase rows.';
            setTimeout(() => focusBulkCell(firstInvalidRow, 'barcode'), 0);
        }

        setBulkErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const resetBulkStaging = () => {
        setBulkFormData(createInitialBulkPurchaseForm());
        bulkRowsRef.current = [];
        setBulkRows([]);
        setBulkErrors({});
        setBulkPage(0);
        bulkCellRefs.current = {};
        setLastScannedBarcode('');
    };

    const addBulkGroupToBill = () => {
        if (!validateBulkForm()) return;

        const groupId = `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const group = {
            id: groupId,
            header: { ...bulkFormData },
            rows: bulkRowsRef.current.map(row => ({ ...row }))
        };
        setBulkGroups(prev => [...prev, group]);
        const addedCount = group.rows.length;
        const addedName = group.header.product_name.trim();
        resetBulkStaging();
        showToast('success', `${addedName} × ${addedCount} added to this bill.`);
        setTimeout(() => document.getElementById('purchase_bulk_product_name')?.focus(), 0);
    };

    const removeBulkGroup = (groupId) => {
        const group = bulkGroups.find(item => item.id === groupId);
        setBulkGroups(prev => prev.filter(item => item.id !== groupId));
        showToast('success', `${group?.header?.product_name || 'Bulk group'} removed from this bill.`);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { showToast('error', 'File size must be less than 5MB'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            setFormData(prev => ({ ...prev, bill_file: { name: file.name, type: file.type, data: ev.target.result.split(',')[1] } }));
        };
        reader.readAsDataURL(file);
    };

    const validateForm = (values = formData) => {
        const errs = {};
        if (!values.bill_no?.trim()) errs.bill_no = 'Bill number is required';
        if (!values.supplierid) errs.supplier = 'Supplier is required';
        if (!values.date) errs.date = 'Date is required';
        if (!values.items || values.items.length === 0) {
            errs.items = 'At least one item is required';
        } else {
            values.items.forEach((item, i) => {
                if (!item.product_name?.trim()) errs[`product_name_${i}`] = 'Product name required';
                if (!item.batch_no?.trim()) errs[`batch_no_${i}`] = 'Batch number required';
                if (!item.price || parseFloat(item.price) <= 0) errs[`price_${i}`] = 'Price required';
                if (!item.quantity || parseFloat(item.quantity) <= 0) errs[`quantity_${i}`] = 'Quantity required';
            });
        }
        const paymentError = purchasePayment.validate();
        if (paymentError) errs.paid = paymentError;
        setErrors(errs);
        const firstError = Object.values(errs).find(Boolean);
        if (firstError) {
            showToast('error', firstError);
            requestAnimationFrame(() => {
                document.querySelector('.ozui-error, .ozui-error-row')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            return false;
        }
        return true;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'paid') {
            const paidValue = parseFloat(value) || 0;
            setFormData(prev => {
                const grandTotal = parseFloat(prev.grand_total) || 0;
                const balance = grandTotal - paidValue;
                return {
                    ...prev,
                    paid: value,
                    balance: balance.toFixed(2)
                };
            });
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const generateNextBillNumber = () => {
        const d = new Date();
        const y = d.getFullYear().toString().substr(-2);
        const m = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const r = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `PUR-${y}${m}${day}-${r}`;
    };

    const handleGenerateBillNumber = () => {
        if (!formData.bill_no) setFormData(prev => ({ ...prev, bill_no: generateNextBillNumber() }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSavingRef.current) { showToast('warning', 'Please wait...'); return; }
        if (!currentShop?.shop_id) { showToast('error', 'No shop selected.'); return; }
        if (hasStagedBulkData && !validateBulkForm()) {
            setEntryMode('bulk');
            showToast('warning', 'Finish the current bulk group before saving the bill.');
            return;
        }

        const committedBulkItems = bulkGroups.flatMap(group =>
            buildBulkPurchaseItems(group.rows, group.header, group.id)
        );
        const stagedGroupId = hasStagedBulkData
            ? `bulk-staged-${Date.now()}`
            : null;
        const stagedBulkItems = hasStagedBulkData
            ? buildBulkPurchaseItems(bulkRowsRef.current, bulkFormData, stagedGroupId)
            : [];
        const sourceItems = [
            ...formData.items.map(item => ({ ...item, entry_kind: item.entry_kind || 'single' })),
            ...committedBulkItems,
            ...stagedBulkItems
        ];
        const bulkItemCount = committedBulkItems.length + stagedBulkItems.length;
        const singleItemCount = formData.items.length;
        const purchaseEntryMode = bulkItemCount > 0 && singleItemCount > 0
            ? 'mixed'
            : bulkItemCount > 0 ? 'bulk' : 'single';
        const submissionGrandTotal = calculateGrandTotal(sourceItems);
        const submissionFormData = {
            ...formData,
            items: sourceItems,
            grand_total: submissionGrandTotal.toFixed(2),
            paid: purchasePayment.currentInvoicePaid.toFixed(2),
            balance: purchasePayment.currentInvoiceBalance.toFixed(2)
        };
        if (!validateForm(submissionFormData)) return;

        isSavingRef.current = true;
        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error('User not authenticated');

            const purchasesEndpoint = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_purchases.php';

            const processedItems = sourceItems.map(item => {
                const isPacking = (item.purchase_type || 'packing') === 'packing';
                const quantityEntered = parseFloat(item.quantity) || 0;
                const itemsPerPack = parseFloat(item.items_per_pack) || 1;

                if (isPacking) {
                    const costPerPack = parseFloat(item.cost_per_pack) || parseFloat(item.price) || 0;
                    const pricePerUnit = itemsPerPack > 0 ? costPerPack / itemsPerPack : costPerPack;
                    const totalLine = costPerPack * quantityEntered;

                    return {
                        entry_kind: item.entry_kind || 'single',
                        bulk_group_id: item.bulk_group_id || null,
                        product_name: item.product_name || '',
                        model: item.model || '',
                        serial_no: item.serial_no || '',
                        batch_no: item.batch_no || '',
                        product_code: item.product_code || '',
                        // ✅ BARCODE: Preserve as string with leading zeros
                        barcode: String(item.barcode ?? '').trim(),
                        category: item.category || '',
                        company: item.company || '',
                        color: item.color || '',
                        size: item.size || '',
                        purchase_type: 'packing',
                        is_sub_packing_purchase: false,
                        packing_type: item.packing_type || null,
                        sub_packing_type: item.sub_packing_type || null,
                        items_per_pack: itemsPerPack,
                        unit_of_measure: item.unit_of_measure || 'piece',
                        price: pricePerUnit,
                        cost_per_pack: costPerPack,
                        sell_per_pack: Math.max(0, parseFloat(item.sell_per_pack) || 0),
                        sell_price_per_unit: Math.max(0, parseFloat(item.sell_price_per_unit) || 0),
                        profit_percent: normalizeOptionalProfitPercent(item.profit_percent),
                        quantity: quantityEntered,
                        total: totalLine.toFixed(2),
                        product_id: item.product_id || null,
                        expiry_date: item.expiry_date || null
                    };
                } else {
                    const pricePerUnit = parseFloat(item.price) || 0;
                    const totalLine = pricePerUnit * quantityEntered;
                    const costPerPack = pricePerUnit * itemsPerPack;

                    return {
                        entry_kind: item.entry_kind || 'single',
                        bulk_group_id: item.bulk_group_id || null,
                        product_name: item.product_name || '',
                        model: item.model || '',
                        serial_no: item.serial_no || '',
                        batch_no: item.batch_no || '',
                        product_code: item.product_code || '',
                        // ✅ BARCODE: Preserve as string with leading zeros
                        barcode: String(item.barcode ?? '').trim(),
                        category: item.category || '',
                        company: item.company || '',
                        color: item.color || '',
                        size: item.size || '',
                        purchase_type: 'sub_packing',
                        is_sub_packing_purchase: true,
                        packing_type: null,
                        sub_packing_type: null,
                        items_per_pack: itemsPerPack,
                        unit_of_measure: item.unit_of_measure || 'piece',
                        price: pricePerUnit,
                        cost_per_pack: costPerPack,
                        sell_per_pack: Math.max(0, parseFloat(item.sell_per_pack) || 0),
                        sell_price_per_unit: Math.max(0, parseFloat(item.sell_price_per_unit) || 0),
                        profit_percent: normalizeOptionalProfitPercent(item.profit_percent),
                        quantity: quantityEntered,
                        total: totalLine.toFixed(2),
                        product_id: item.product_id || null,
                        expiry_date: item.expiry_date || null
                    };
                }
            });

            const payload = {
                action: isEditMode ? 'edit' : 'add',
                entry_mode: purchaseEntryMode,
                rq: bulkItemCount,
                bulk_group_count: bulkGroups.length + (stagedBulkItems.length > 0 ? 1 : 0),
                username: currentUsername,
                shop_id: parseInt(currentShop.shop_id),
                date: submissionFormData.date,
                bill_no: submissionFormData.bill_no,
                supplierid: parseInt(submissionFormData.supplierid) || 0,
                supplier_name: submissionFormData.supplier_name,
                phone: submissionFormData.phone || '',
                address: submissionFormData.address || '',
                grand_total: parseFloat(submissionFormData.grand_total || 0).toFixed(2),
                paid: purchasePayment.currentInvoicePaid.toFixed(2),
                ...purchasePayment.payload,
                items: processedItems,
                status: parseFloat(submissionFormData.balance || 0) > 0 ? 'pending' : 'paid'
            };

            if (formData.bill_file) payload.bill_file = JSON.stringify(formData.bill_file);
            if (isEditMode && purchaseId) payload.id = purchaseId;

            const response = await fetch(purchasesEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                cache: 'no-store',
                body: JSON.stringify(payload)
            });
            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (parseError) {
                const preview = responseText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240);
                throw new Error(preview || `Server returned an invalid response (HTTP ${response.status})`);
            }
            if (!response.ok) {
                throw new Error(data.message || `Server error (HTTP ${response.status})`);
            }

            if (data.status === 'success') {
                if (data.handler_version !== 'barcode-exact-v4') {
                    console.warn('Purchase saved, but handler version metadata was missing or different.');
                }

                const savedBarcodes = Array.isArray(data.saved_barcodes) ? data.saved_barcodes : [];
                if (savedBarcodes.length > 0) {
                    processedItems.forEach((item, index) => {
                        const expected = String(item.barcode ?? '').trim();
                        if (!expected) return;

                        const saved = savedBarcodes[index];
                        const purchaseItemBarcode = String(saved?.purchase_item_barcode ?? '');
                        const productBarcode = String(saved?.product_barcode ?? '');

                        if (purchaseItemBarcode !== expected || productBarcode !== expected) {
                            throw new Error(
                                `Barcode verification failed for ${item.product_name || `item ${index + 1}`}. ` +
                                `Expected ${expected}, purchase item has ${purchaseItemBarcode || '(blank)'}, ` +
                                `product has ${productBarcode || '(blank)'}.`
                            );
                        }
                    });
                }

                showToast('success', data.message || (isEditMode
                    ? translations?.purchaseUpdated || 'Purchase updated!'
                    : translations?.purchaseSaved || 'Purchase saved!'));

                if (!isEditMode) {
                    setFormData({ ...initialFormData, date: new Date().toISOString().split('T')[0], items: [] });
                    purchasePayment.resetPayments();
                    setBulkFormData(createInitialBulkPurchaseForm());
                    bulkRowsRef.current = [];
                    setBulkRows([]);
                    setBulkGroups([]);
                    setBulkErrors({});
                    setBulkPage(0);
                    bulkCellRefs.current = {};
                    setSearchTerm('');
                    setErrors({});
                    setGlobalProductSearchTerm('');
                    setTimeout(() => {
                        if (entryMode === 'bulk') document.getElementById('purchase_bulk_product_name')?.focus();
                        else if (billNumberRef.current) billNumberRef.current.focus();
                    }, 100);
                }

                if (fileInputRef.current) fileInputRef.current.value = '';
                if (onSuccess) onSuccess(data.purchaseid || purchaseId);
            } else {
                showToast('error', data.message || 'Failed to save purchase');
            }
        } catch (error) {
            console.error('Purchase submission error:', error);
            showToast('error', error.message || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode
                                ? (translations?.editPurchase || 'Edit Purchase')
                                : (translations?.addPurchase || 'New Purchase')}</h1>
                            <p>{isEditMode ? 'Purchase Details' : 'Build one bill with Single and Bulk product groups'}</p>
                        </div>
                    </div>
                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={clearDraft}
                                disabled={saving}
                                title={translations?.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations?.clear || 'Clear'}</span>
                            </button>
                            <button
                                className="ozui-action-btn save-draft"
                                onClick={saveDraft}
                                disabled={saving}
                                title={translations?.saveDraft || 'Save Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations?.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">{draft.bill_no || `Purchase #${draft.id}`}</div>
                                                            <div className="draft-details">
                                                                {draft.supplier_name && <span>{draft.supplier_name}</span>}
                                                                <span>{(draft.bulkGroups?.length || 0) > 0 && (draft.items?.length || 0) > 0 ? 'Mixed bill' : (draft.bulkGroups?.length || 0) > 0 ? 'Bulk bill' : 'Single bill'}</span>
                                                                <span>{draft.items?.length || 0} single</span>
                                                                <span>{draft.bulkGroups?.length || 0} bulk groups</span>
                                                            </div>
                                                            <div className="draft-date">{new Date(draft.savedAt).toLocaleDateString()}</div>
                                                        </div>
                                                        <button className="delete-draft-btn" onClick={e => { e.stopPropagation(); deleteDraft(draft.id); }}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                className="ozui-action-btn scanner-toggle"
                                onClick={() => setIsScannerActive(!isScannerActive)}
                                title="Toggle Scanner"
                            >
                                <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                                <span>{isScannerActive ? 'Scanner ON' : 'Scanner OFF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

         

            <input
                ref={barcodeInputRef}
                type="text"
                id="barcode_scanner_hidden"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, width: '1px', height: '1px' }}
                autoComplete="off"
                tabIndex={-1}
                onChange={() => { }}
            />

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input type="date" name="date" id="date" value={formData.date} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="date"><FontAwesomeIcon icon={faCalendarAlt} />{translations?.date || 'Date'} <span>*</span></label>
                        </div>
                        {errors.date && <small>{errors.date}</small>}
                    </div>

                    <div className={`ozui-field ${errors.bill_no ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper ozui-bill-input-wrapper">
                            <input ref={billNumberRef} type="text" name="bill_no" id="bill_no" value={formData.bill_no} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="bill_no"><FontAwesomeIcon icon={faFileInvoice} />{translations?.billNumber || 'Bill Number'} <span>*</span></label>
                            {!formData.bill_no && !saving && (
                                <button type="button" className="ozui-input-generate-btn" onClick={handleGenerateBillNumber} title="Generate">
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            )}
                        </div>
                        {errors.bill_no && <small>{errors.bill_no}</small>}
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.supplier ? 'ozui-error' : ''}`} ref={supplierSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input type="text" id="supplier_search" value={searchTerm} onChange={e => handleSupplierSearch(e.target.value)} onFocus={() => setShowSupplierDropdown(true)} placeholder=" " disabled={saving} autoComplete="off" />
                            <label htmlFor="supplier_search"><FontAwesomeIcon icon={faUser} />{translations?.supplier || 'Supplier'} <span>*</span></label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {showSupplierDropdown && (
                            <div className="ozui-supplier-dropdown">
                                {filteredSuppliers && filteredSuppliers.length > 0 ? (
                                    filteredSuppliers.map(s => (
                                        <div key={s.supplierid} className="ozui-dropdown-item" onClick={() => selectSupplier(s)}>
                                            <div className="ozui-supplier-name">{s.supplier_name}</div>
                                            {s.phone && <div className="ozui-supplier-phone"><FontAwesomeIcon icon={faPhone} /> {s.phone}</div>}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-dropdown-empty">{translations?.noSuppliersFound || 'No suppliers found'}</div>
                                )}
                            </div>
                        )}
                        {errors.supplier && <small>{errors.supplier}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="text" name="supplier_name" id="supplier_name" value={formData.supplier_name} readOnly placeholder=" " className="ozui-readonly-input" />
                            <label htmlFor="supplier_name"><FontAwesomeIcon icon={faStore} />{translations?.supplierName || 'Supplier Name'}</label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="phone"><FontAwesomeIcon icon={faPhone} />{translations?.phone || 'Phone'} <span>({translations?.optional || 'Optional'})</span></label>
                        </div>
                    </div>
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="text" name="address" id="address" value={formData.address} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="address"><FontAwesomeIcon icon={faMapMarkerAlt} />{translations?.address || 'Address'} <span>({translations?.optional || 'Optional'})</span></label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper ozui-file-wrapper">
                            <input type="file" id="bill_file" ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" disabled={saving} className="ozui-file-input" />
                            <label htmlFor="bill_file">
                                <FontAwesomeIcon icon={faUpload} />{translations?.billFile || 'Bill File'}
                                <span>({translations?.optional || 'Optional'})</span>
                            </label>
                            <span className="ozui-file-name">{formData.bill_file ? formData.bill_file.name : (translations?.noFile || 'No file chosen')}</span>
                        </div>
                    </div>
                </div>

                {entryMode === 'single' ? (
                    <>
                        <div className="ozui-form-row">
                            <div className="ozui-field" ref={productSearchRef}>
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        id="global_product_search"
                                        value={globalProductSearchTerm}
                                        onChange={e => { setGlobalProductSearchTerm(e.target.value); searchGlobalProducts(e.target.value); }}
                                        onFocus={() => globalProductSearchTerm.length >= 2 && setShowGlobalProductDropdown(true)}
                                        placeholder=" "
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                                    <label htmlFor="global_product_search">
                                        <FontAwesomeIcon icon={faSearch} />{translations?.searchProducts || 'Search Products'}
                                    </label>
                                    {searchingGlobalProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                                </div>
                                {showGlobalProductDropdown && filteredGlobalProducts.length > 0 && (
                                    <div className="ozui-product-dropdown">
                                        {filteredGlobalProducts.map(product => (
                                            <div key={product.id} className="ozui-product-item" onClick={() => addProductToItems(product)}>
                                                <div className="ozui-product-item-name">
                                                    {product.product_name}
                                                    {product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}
                                                </div>
                                                <div className="ozui-product-item-details">
                                                    {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                                    {product.category && <span><FontAwesomeIcon icon={faBox} /> {product.category}</span>}
                                                </div>
                                                <div className="ozui-product-item-price-stock">
                                                    <span className="ozui-product-price">
                                                        <FontAwesomeIcon icon={faRupeeSign} />{' '}
                                                        {(product.purchase_type === 'sub_packing'
                                                            ? parseFloat(product.cost_per_unit || product.cost_price || 0)
                                                            : parseFloat(product.cost_per_pack || product.cost_price || 0)
                                                        ).toFixed(2)}
                                                        {product.purchase_type === 'sub_packing' ? ' /unit' : ' /pack'}
                                                    </span>
                                                    <span className={`ozui-product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                        Stock: {product.quantity} packs / {parseBQuantityNumeric(product.bquantity || '0').toFixed(2)} units
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <ItemsTable
                            items={formData.items}
                            onItemChange={handleItemChange}
                            onRemove={removeItem}
                            onAdd={addItem}
                            errors={errors}
                            disabled={saving}
                            translations={translations}
                            isRTL={isRTL}
                            user={user}
                            shopId={currentShop?.shop_id}
                            showToast={showToast}
                            allowEmpty={bulkGroups.length > 0 || hasStagedBulkData}
                        />
                    </>
                ) : (
                    <div className="ozui-purchase-bulk-section">
                        <div className="ozui-purchase-bulk-summary-row">
                            <div className={`ozui-field ${bulkErrors.product_name ? 'ozui-error' : ''}`}>
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        name="product_name"
                                        id="purchase_bulk_product_name"
                                        value={bulkFormData.product_name}
                                        onChange={handleBulkFieldChange}
                                        placeholder=" "
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                                    <label htmlFor="purchase_bulk_product_name"><FontAwesomeIcon icon={faBox} /> Product Name <span>*</span></label>
                                </div>
                                {bulkErrors.product_name && <small>{bulkErrors.product_name}</small>}
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        name="model"
                                        id="purchase_bulk_model"
                                        value={bulkFormData.model}
                                        onChange={handleBulkFieldChange}
                                        placeholder=" "
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                                    <label htmlFor="purchase_bulk_model">Model (Optional)</label>
                                </div>
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        name="serial_no"
                                        id="purchase_bulk_serial_no"
                                        value={bulkFormData.serial_no}
                                        onChange={handleBulkFieldChange}
                                        placeholder=" "
                                        disabled={saving}
                                        autoComplete="off"
                                    />
                                    <label htmlFor="purchase_bulk_serial_no">Serial No. (Optional)</label>
                                </div>
                            </div>

                            <div className={`ozui-field ${bulkErrors.rq ? 'ozui-error' : ''}`}>
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="number"
                                        name="rq"
                                        id="purchase_bulk_rq"
                                        value={bulkFormData.rq}
                                        onChange={handleBulkFieldChange}
                                        min="1"
                                        step="1"
                                        inputMode="numeric"
                                        placeholder=" "
                                        disabled={saving}
                                    />
                                    <label htmlFor="purchase_bulk_rq"><FontAwesomeIcon icon={faBoxes} /> RQ (Rows) <span>*</span></label>
                                </div>
                                {bulkErrors.rq && <small>{bulkErrors.rq}</small>}
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper ozui-purchase-bulk-select-wrapper">
                                    <select
                                        name="category"
                                        id="purchase_bulk_category"
                                        value={bulkFormData.category}
                                        onChange={handleBulkFieldChange}
                                        disabled={saving}
                                    >
                                        {categoryOptions.map(category => (
                                            <option key={category.value} value={category.value}>{category.label}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="purchase_bulk_category"><FontAwesomeIcon icon={faTag} /> Category</label>
                                </div>
                            </div>

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="text"
                                        name="company"
                                        id="purchase_bulk_company"
                                        value={bulkFormData.company}
                                        onChange={handleBulkFieldChange}
                                        placeholder=" "
                                        disabled={saving}
                                    />
                                    <label htmlFor="purchase_bulk_company"><FontAwesomeIcon icon={faBuilding} /> Company</label>
                                </div>
                            </div>

                            <div className={`ozui-field ${bulkErrors.cost_price ? 'ozui-error' : ''}`}>
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="number"
                                        name="cost_price"
                                        id="purchase_bulk_cost_price"
                                        value={bulkFormData.cost_price}
                                        onChange={handleBulkFieldChange}
                                        min="0"
                                        step="0.01"
                                        placeholder=" "
                                        disabled={saving}
                                    />
                                    <label htmlFor="purchase_bulk_cost_price"><FontAwesomeIcon icon={faRupeeSign} /> Cost Price <span>*</span></label>
                                </div>
                                {bulkErrors.cost_price && <small>{bulkErrors.cost_price}</small>}
                            </div>

                           

                            <div className="ozui-field">
                                <div className="ozui-input-wrapper">
                                    <input
                                        type="number"
                                        name="sell_price"
                                        id="purchase_bulk_sell_price"
                                        value={bulkFormData.sell_price}
                                        onChange={handleBulkFieldChange}
                                        min="0"
                                        step="0.01"
                                        placeholder=" "
                                        disabled={saving}
                                    />
                                    <label htmlFor="purchase_bulk_sell_price"><FontAwesomeIcon icon={faRupeeSign} /> Sell Price ({translations?.optional || 'Optional'})</label>
                                </div>
                            </div>
                        </div>

                        <div className="ozui-purchase-bulk-toolbar" aria-live="polite">
                            <span><FontAwesomeIcon icon={faBarcode} /> <strong>Global scanner ready</strong> — focus any barcode row or scan continuously.</span>
                            <span>{scannedBulkCount} / {bulkRows.length} scanned</span>
                        </div>

                        {bulkErrors.rows && (
                            <div className="ozui-purchase-bulk-warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} /> {bulkErrors.rows}
                            </div>
                        )}

                        <div className="ozui-purchase-bulk-table-container">
                            <table className="ozui-purchase-bulk-table">
                                <thead>
                                    <tr>
                                        <th className="ozui-purchase-bulk-sr-col">Sr</th>
                                        <th>Batch No</th>
                                        <th>Product Name</th>
                                        <th>Total Quantity</th>
                                        <th>Barcode</th>
                                        <th>Company</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkRows.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="ozui-purchase-bulk-empty">Enter RQ above to create purchase rows.</td>
                                        </tr>
                                    ) : visibleBulkRows.map((row, pageRowIndex) => {
                                        const rowIndex = bulkPageStart + pageRowIndex;
                                        return (
                                            <tr key={rowIndex}>
                                                <td className="ozui-purchase-bulk-sr-cell">{rowIndex + 1}</td>
                                                <td>
                                                    <input
                                                        ref={element => { bulkCellRefs.current[`${rowIndex}-batch_no`] = element; }}
                                                        className={`ozui-purchase-bulk-input ${bulkErrors[`batch_no_${rowIndex}`] ? 'ozui-invalid' : ''}`}
                                                        value={row.batch_no}
                                                        onChange={event => handleBulkRowChange(rowIndex, 'batch_no', event.target.value)}
                                                        disabled={saving}
                                                        title={bulkErrors[`batch_no_${rowIndex}`] || 'Unique batch number'}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        ref={element => { bulkCellRefs.current[`${rowIndex}-product_name`] = element; }}
                                                        className="ozui-purchase-bulk-input ozui-purchase-bulk-readonly"
                                                        value={bulkFormData.product_name}
                                                        readOnly
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        ref={element => { bulkCellRefs.current[`${rowIndex}-total_quantity`] = element; }}
                                                        type="number"
                                                        className={`ozui-purchase-bulk-input ${bulkErrors[`total_quantity_${rowIndex}`] ? 'ozui-invalid' : ''}`}
                                                        value={row.total_quantity}
                                                        onChange={event => handleBulkRowChange(rowIndex, 'total_quantity', event.target.value)}
                                                        min="0.001"
                                                        step="0.001"
                                                        disabled={saving}
                                                        title={bulkErrors[`total_quantity_${rowIndex}`] || ''}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        ref={element => { bulkCellRefs.current[`${rowIndex}-barcode`] = element; }}
                                                        className={`ozui-purchase-bulk-input ozui-purchase-bulk-barcode ${bulkErrors[`barcode_${rowIndex}`] ? 'ozui-invalid' : ''}`}
                                                        value={row.barcode}
                                                        onChange={event => handleBulkRowChange(rowIndex, 'barcode', event.target.value)}
                                                        data-purchase-bulk-barcode-index={rowIndex}
                                                        autoComplete="off"
                                                        disabled={saving}
                                                        title={bulkErrors[`barcode_${rowIndex}`] || 'Scan or type a unique barcode'}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        ref={element => { bulkCellRefs.current[`${rowIndex}-company`] = element; }}
                                                        className="ozui-purchase-bulk-input ozui-purchase-bulk-readonly"
                                                        value={bulkFormData.company}
                                                        readOnly
                                                        onKeyDown={event => handleBulkCellKeyDown(event, rowIndex, 'company')}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {bulkRows.length > BULK_PURCHASE_PAGE_SIZE && (
                            <div className="ozui-purchase-bulk-pagination">
                                <button type="button" onClick={() => setBulkPage(0)} disabled={safeBulkPage === 0}>First</button>
                                <button type="button" onClick={() => setBulkPage(page => Math.max(0, page - 1))} disabled={safeBulkPage === 0}>Previous</button>
                                <span>Rows {bulkPageStart + 1}–{Math.min(bulkPageStart + BULK_PURCHASE_PAGE_SIZE, bulkRows.length)} of {bulkRows.length} · Page {safeBulkPage + 1} of {bulkPageCount}</span>
                                <button type="button" onClick={() => setBulkPage(page => Math.min(bulkPageCount - 1, page + 1))} disabled={safeBulkPage >= bulkPageCount - 1}>Next</button>
                                <button type="button" onClick={() => setBulkPage(bulkPageCount - 1)} disabled={safeBulkPage >= bulkPageCount - 1}>Last</button>
                            </div>
                        )}

                        <div className="ozui-purchase-bulk-group-actions">
                            <span>Add this group to the current bill, then enter the next Bulk or Single product.</span>
                            <button
                                type="button"
                                className="ozui-btn ozui-btn-secondary"
                                onClick={resetBulkStaging}
                                disabled={saving || !hasStagedBulkData}
                            >
                                <FontAwesomeIcon icon={faUndo} /> Clear Current Group
                            </button>
                            <button
                                type="button"
                                className="ozui-btn ozui-btn-primary"
                                onClick={addBulkGroupToBill}
                                disabled={saving || bulkRows.length === 0}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Add Bulk Group to Bill
                            </button>
                        </div>
                    </div>
                )}

                {bulkGroups.length > 0 && (
                    <div className="ozui-bill-bulk-groups">
                        <div className="ozui-bill-bulk-groups-header">
                            <div>
                                <strong>Bulk groups in this bill</strong>
                                <span>Each row will be saved as a separate physical product under this purchase.</span>
                            </div>
                            <span>{committedBulkRowCount} physical rows</span>
                        </div>
                        <div className="ozui-bill-bulk-groups-table-wrap">
                            <table className="ozui-bill-bulk-groups-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Product</th>
                                        <th>Rows</th>
                                        <th>Total Qty</th>
                                        <th>Scanned</th>
                                        <th>Cost</th>
                                        <th>Group Total</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkGroups.map((group, index) => {
                                        const totalQty = group.rows.reduce((sum, row) => sum + (parseFloat(row.total_quantity) || 0), 0);
                                        const scanned = group.rows.reduce((sum, row) => sum + (row.barcode?.trim() ? 1 : 0), 0);
                                        const cost = parseFloat(group.header.cost_price) || 0;
                                        return (
                                            <tr key={group.id}>
                                                <td>{index + 1}</td>
                                                <td><strong>{group.header.product_name}</strong></td>
                                                <td>{group.rows.length}</td>
                                                <td>{formatEditableNumber(totalQty, 3)}</td>
                                                <td>{scanned}/{group.rows.length}</td>
                                                <td>Rs. {cost.toFixed(2)}</td>
                                                <td>Rs. {(cost * totalQty).toFixed(2)}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="ozui-bulk-group-remove"
                                                        onClick={() => removeBulkGroup(group.id)}
                                                        disabled={saving}
                                                        title="Remove this entire bulk group"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} /> Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {(formData.items.length > 0 || bulkGroups.length > 0 || hasStagedBulkData) && (
                    <div className="ozui-form-section">
                        <SalePaymentSection
                            payment={purchasePayment}
                            direction="out"
                            title="Supplier Payment Details"
                            helpText="Pay from one account or split the amount across multiple methods."
                            subtotal={Number(formData.grand_total) || 0}
                            previousBalance={purchasePayment.previousBalance}
                            creditBalance={purchasePayment.creditBalance}
                            payableTotal={purchasePayment.payableTotal}
                            paidTotal={purchasePayment.paidTotal}
                            balanceAfter={purchasePayment.balanceAfter}
                            showDiscount={false}
                            referenceEnabled
                            disabled={saving}
                            error={errors.paid}
                            translations={{
                                paymentMethod: 'Payment Methods',
                                previousBalance: 'Previous Supplier Balance',
                                subtotal: 'Current Purchase Total',
                                grandTotal: 'Total Payable',
                                paidAmount: 'Total Paid Now',
                                balance: 'Supplier Balance After'
                            }}
                        />
                    </div>
                )}

                <div className="ozui-form-actions">
                    <button type="submit" className="ozui-btn ozui-btn-primary" disabled={saving || totalBillRowCount === 0}>
                        {saving ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /><span>{translations?.saving || 'Saving...'}</span></>
                        ) : (
                            <><FontAwesomeIcon icon={faSave} /><span>{isEditMode
                                ? (translations?.updatePurchase || 'Update Purchase')
                                : `Save Bill (${totalBillRowCount} product row${totalBillRowCount === 1 ? '' : 's'})`}</span></>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}

            <style>{`
                .ozui-scanner-status {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    z-index: 9999;
                    padding: 8px 16px;
                    border-radius: 20px;
                    background: rgba(0, 0, 0, 0.8);
                    color: white;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    pointer-events: none;
                    backdrop-filter: blur(10px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                }
                .ozui-scanner-indicator.active { color: #10b981; }
                .ozui-scanner-indicator.inactive { color: #ef4444; }
                .ozui-scanner-indicator { display: flex; align-items: center; gap: 6px; }
            `}</style>
        </div>
    );
};

export default AppliancesPurchases;
export { ItemsTable as PurchaseItemsTable, useBarcodeScanner };
